import { useState, useMemo, useEffect, useCallback, useRef, Fragment } from 'react';
import { usePullToRefresh } from '../../utils/pullToRefresh/usePullToRefresh';
import { PullToRefreshIndicator } from '../../utils/pullToRefresh/PullToRefreshIndicator';
import { useAuth } from '../../context/AuthContext';
import { useBranch } from '../../context/BranchContext';
import { getCurrencySymbol, formatMoneyForExport, formatGroupedMonetary, moneyHeader } from '../../utils/currency';
import { useDesktopTopBar } from '../../context/DesktopLayoutContext';
import DesktopLayout from '../layout/DesktopLayout';
import TopBar, { DATE_PRESETS_DESKTOP_NO_TODAY, DATE_PRESETS_MOBILE_NO_TODAY } from '../layout/TopBar';
import Phone from '../Phone';
import BottomNav from '../BottomNav';
import Icon from '../Icon';
import { C } from '../theme';
import {
    getPopularRevenueTop,
    getPopularRevenueBottom,
    getSalesByRevenueClass,
    getProductCancelledItems,
    getProductItemsCancelledReasons,
    getProductCategoryItemSales,
    getProductTotalsRevenue,
    getCategoryChannelSummary,
    getCategoryDropdown,
    getModifierAnalysis,
    getModifierGroups,
    getModifierItems,
    toDateStr,
    todayStr,
    yesterdayStr,
} from '../../services/reportsApi';
import { useStoreTimeZone, getStoreTimeZone, getYesterdayRange, get7DayRange, get30DayRange, getThisMonthRange } from '../../utils/dateTimeUtils';
import { useSharedReportDateFilter } from '../../redux/reportFilters/reportFiltersHooks';
import { exportPeriodFor } from '../../utils/reportPeriod';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend } from 'recharts';
import './reports.css';
import { T, useIsDesktop, Bone } from "./reportTokens";
import SectionError from "./SectionError";
import SectionEmpty from "./SectionEmpty";
import { useSwipeBack } from '../../hooks/useSwipeBack';
import KpiTooltip from '../KpiTooltip';
import { ReportSectionExportButton } from './components/ReportExportButton';
import { getLiveCategorySalesApi } from '../../redux/reports/clickHouse/clickHouseApi';
import type { LiveCategorySalesItem } from '../../interface/clickHouseInterface';

// Sales by Category card (ZAR-1223) — replaces the legacy PMIX Top-15 chart.
// "All" sends one large page so the backend returns every category at once.
const CAT_ALL_SIZE = 100000;
const CAT_PAGE_SIZE_OPTIONS: (number | 'All')[] = [5, 10, 20, 'All'];
// Modifier Performance table — server-side pagination/sorting via page/size/sortBy/sortDir.
const MODIFIER_PAGE_SIZE_OPTIONS: (number | 'All')[] = [5, 10, 20, 'All'];
// "All" sends one large page so the backend returns everything at once (export uses this too).
const MODIFIER_ALL_SIZE = 100000;
// SSE-19: quantity cells in the Sales by Category export — integers stay whole,
// fractional values show 2–3 decimals (42 → "42", 42.2 → "42.20", 42.201 → "42.201").
// Returned as a string so the CSV escaper's force-text guard keeps the trailing zero.
const fmtExportQty = (n: number | null | undefined): string => {
    const v = Number(n) || 0;
    if (Number.isInteger(v)) return String(v);
    const decimals = (String(v).split('.')[1] || '').length;
    return v.toFixed(Math.min(Math.max(decimals, 2), 3));
};

// Self-contained data hook for the Sales by Category card. This screen is always
// historical (no Today) and has no channel filter, so startDate/endDate are
// required and orderSource is omitted (Overall). Drives a server-paginated page
// plus a one-shot fetch of all categories for the running total + export.
function useCategorySalesData(locationId: string, token: string | null, startDate: string, endDate: string) {
    const [items, setItems] = useState<LiveCategorySalesItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [failure, setFailure] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState<number | 'All'>(5);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [allItems, setAllItems] = useState<LiveCategorySalesItem[]>([]);
    const [retryTick, setRetryTick] = useState(0);
    const allKeyRef = useRef<string | null>(null);
    const reqSize = pageSize === 'All' ? CAT_ALL_SIZE : pageSize;
    const grandTotal = useMemo(() => allItems.reduce((s, it) => s + (it.price || 0), 0), [allItems]);
    const retry = useCallback(() => setRetryTick((t) => t + 1), []);
    // Fetch the whole set (all pages) at export time, like other sections.
    // When `overrideLocationId` is passed (outlet-scoped export for a DIFFERENT
    // outlet), `totalElements` above is the CURRENT outlet's count and is not
    // valid for the override outlet — probe with size:1 first to learn its
    // real total, then fetch everything in a second call.
    const fetchAll = useCallback(async (overrideLocationId?: string): Promise<LiveCategorySalesItem[]> => {
        const loc = overrideLocationId ?? locationId;
        if (!loc || !token || !startDate || !endDate) return [];
        if (overrideLocationId) {
            const probe: any = await getLiveCategorySalesApi({ locationId: loc, page: 1, size: 1, startDate, endDate, token });
            const probeTotal = Number(probe?.totalElements ?? probe?.total ?? (Array.isArray(probe) ? probe.length : 0)) || 0;
            const res: any = await getLiveCategorySalesApi({ locationId: loc, page: 1, size: probeTotal || CAT_ALL_SIZE, startDate, endDate, token });
            return Array.isArray(res) ? res : (res?.categories ?? res?.data ?? []);
        }
        const res: any = await getLiveCategorySalesApi({ locationId: loc, page: 1, size: totalElements || CAT_ALL_SIZE, startDate, endDate, token });
        return Array.isArray(res) ? res : (res?.categories ?? res?.data ?? []);
    }, [locationId, token, startDate, endDate, totalElements]);

    // Reset to the first page whenever the period or page size changes.
    useEffect(() => { setPage(0); }, [locationId, startDate, endDate, pageSize]);

    // Paged fetch (page/size passed to the backend).
    useEffect(() => {
        if (!locationId || !token || !startDate || !endDate) return;
        let cancelled = false;
        setLoading(true);
        setFailure(false);
        getLiveCategorySalesApi({ locationId, page: page + 1, size: reqSize, startDate, endDate, token })
            .then((res: any) => {
                if (cancelled) return;
                const cats = Array.isArray(res) ? res : (res?.categories ?? res?.data ?? []);
                setItems(cats);
                const total = res?.totalElements ?? cats.length;
                setTotalElements(total);
                setTotalPages(res?.totalPages ?? (reqSize ? Math.max(1, Math.ceil(total / reqSize)) : 1));
                setLoading(false);
            })
            .catch(() => { if (!cancelled) { setItems([]); setFailure(true); setLoading(false); } });
        return () => { cancelled = true; };
    }, [locationId, token, startDate, endDate, page, reqSize, retryTick]);

    // All categories — running total + export.
    useEffect(() => {
        if (!locationId || !token || !startDate || !endDate) return;
        const key = `${locationId}|${startDate}|${endDate}`;
        if (allKeyRef.current === key) return;
        allKeyRef.current = key;
        let cancelled = false;
        getLiveCategorySalesApi({ locationId, page: 1, size: CAT_ALL_SIZE, startDate, endDate, token })
            .then((res: any) => { if (!cancelled) setAllItems(Array.isArray(res) ? res : (res?.categories ?? res?.data ?? [])); })
            .catch(() => { if (!cancelled) setAllItems([]); });
        return () => { cancelled = true; };
    }, [locationId, token, startDate, endDate]);

    return { items, loading, failure, page, setPage, pageSize, setPageSize, reqSize, totalElements, totalPages, allItems, grandTotal, retry, fetchAll };
}

function ProductDesktopShimmer() {
    const kpiLabels = ["Total Item Revenue", "Total Items Sold", "Avg Revenue / Item"];
    return (
        <main className="rpt-page" style={{ color: T.t1 }}>
            <div className="rpt-flex-col rpt-gap-24" style={{ fontFamily: T.font }}>
                {/* KPI Row */}
                <div className="rpt-flex-row rpt-gap-16">
                    {kpiLabels.map((label) => (
                        <div key={label} className="rpt-tile rpt-card" style={{ flex: 1 }}>
                            <div className="rpt-tile__label" style={{ marginBottom: 10 }}>{label}</div>
                            <Bone h={32} r={6} mb={6} />
                            <Bone h={14} w={80} r={4} />
                        </div>
                    ))}
                </div>
                {/* Top Items + Revenue Streams */}
                <div className="rpt-flex-row rpt-gap-16">
                    <div className="rpt-card--r16 rpt-card" style={{ flex: 1.2 }}>
                        <div className="rpt-section-title">Top 10 Items by Revenue</div>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                            <div key={i} className="rpt-flex-row rpt-flex-center rpt-gap-10" style={{ marginBottom: 10 }}>
                                <Bone w={20} h={20} r={5} />
                                <Bone w={140} h={14} r={4} style={{ flex: 1 }} />
                                <Bone w={60} h={14} r={4} />
                            </div>
                        ))}
                    </div>
                    <div className="rpt-card--r16 rpt-card" style={{ flex: 0.8 }}>
                        <div className="rpt-section-title">Top Revenue Streams</div>
                        <Bone h={220} r={8} mb={16} />
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="rpt-flex-row rpt-flex-center rpt-gap-8" style={{ marginBottom: 8 }}>
                                <Bone w={8} h={8} r={2} />
                                <Bone w={90} h={12} r={4} />
                                <Bone w={40} h={12} r={4} style={{ marginLeft: 'auto' }} />
                            </div>
                        ))}
                    </div>
                </div>
                {/* Sales by Category (ZAR-1223) — replaces the legacy PMIX shimmer block */}
                <div className="rpt-card--r16 rpt-card">
                    <div className="rpt-section-title">Sales by Category</div>
                    <Bone h={200} r={8} />
                </div>
                {/* Item Revenue by Channel */}
                <div className="rpt-card--r16 rpt-card">
                    <div className="rpt-section-title">Item Revenue by Channel</div>
                    <Bone h={200} r={8} />
                </div>
            </div>
        </main>
    );
}

function ProductMobileShimmer() {
    const kpiLabels = ["Item Revenue", "Items Sold", "Avg / Item"];
    return (
        <div className="rpt-flex-col rpt-gap-8">
            {/* KPI Strip */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                {kpiLabels.map((label) => (
                    <div key={label} className="rpt-card--r12 rpt-card" style={{ padding: '12px 10px', textAlign: 'center' }}>
                        <div style={{ fontSize: 8, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>{label}</div>
                        <Bone h={20} r={5} />
                    </div>
                ))}
            </div>
            {/* Top Items */}
            <div className="rpt-card--r16 rpt-card" style={{ padding: 14 }}>
                <div className="rpt-section-title">Top 10 by Revenue</div>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="rpt-flex-row rpt-flex-center rpt-gap-8" style={{ marginBottom: 8 }}>
                        <Bone w={18} h={18} r={4} />
                        <Bone h={10} r={4} style={{ flex: 1 }} />
                        <Bone w={50} h={12} r={4} />
                    </div>
                ))}
            </div>
            {/* Top Revenue Streams */}
            <div className="rpt-card--r16 rpt-card" style={{ padding: 14 }}>
                <div className="rpt-section-title">Top Revenue Streams</div>
                <Bone h={160} r={8} mb={10} />
                {[1, 2, 3].map((i) => (
                    <div key={i} className="rpt-flex-row rpt-flex-center rpt-gap-6" style={{ marginBottom: 6 }}>
                        <Bone w={7} h={7} r={2} />
                        <Bone w={80} h={10} r={4} />
                        <Bone w={35} h={10} r={4} style={{ marginLeft: 'auto' }} />
                    </div>
                ))}
            </div>
            {/* Sales by Category (ZAR-1223) — replaces the legacy PMIX shimmer block */}
            <div className="rpt-card--r16 rpt-card" style={{ padding: 14 }}>
                <div className="rpt-section-title">Sales by Category</div>
                <Bone h={140} r={8} />
            </div>
        </div>
    );
}

// ─── Design tokens & styles (no static mock data; data comes from API state below) ───
const categoryColors = ['#0EA5E9', '#FACC15', '#F97316', '#22C55E', '#A855F7', '#EC4899'];

const channelColors = { dinein: '#D23A3B', delivery: '#E86B6B', doordash: '#D97706', ubereats: '#16A34A' };
const portalChannelColors = {
    grubhub: '#FF8C00',
    swiggy: '#FF5200',
    zomato: '#EF4F5F',
    doordash: '#EE2637',
    seamless: '#C90117',
    ubereats: '#06C167',
    gloriafood: '#86BE40',
    pickup: '#1F77B4',
    dinein: '#67833E',
    delivery: '#3FE1C0',
    directonline: '#C9CC3F',
    instore: '#14A789',
};
const fallbackChannelPalette = ['#D23A3B', '#E86B6B', '#D97706', '#16A34A', '#7C5CFC', '#0EA5E9', '#EC4899', '#14A789'];
const normalizeChannelKey = (name: any) => String(name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
const formatChannelLabel = (key: any) =>
    String(key ?? '')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([a-z])([0-9])/g, '$1 $2')
        .replace(/([0-9])([a-z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());
const getChannelColor = (key: any, idx: any = 0) => channelColors[key] ?? portalChannelColors[key] ?? fallbackChannelPalette[idx % fallbackChannelPalette.length];

// Historical presets are anchored on the store-timezone anchor date (5 AM cutoff),
// not the raw `yesterday` arg — see getAnchorDate / DateProdIssue.md.
const getStartEndFromFilter = (filter: any, today: any, yesterday: any, timeZoneCd: string = getStoreTimeZone()) => {
    const raw = typeof filter === "string" ? filter.trim() : "";
    const normalized = raw.toLowerCase().replace(/\s+/g, "");
    if (raw && raw.startsWith('custom:')) {
        const p = filter.split(':');
        if (p.length === 3) return { startDate: p[1], endDate: p[2] };
    }
    if (normalized === 'today') return { startDate: today, endDate: today };
    if (normalized === 'yesterday') return getYesterdayRange(timeZoneCd);
    if (normalized === '7d') return get7DayRange(timeZoneCd);
    if (normalized === '30d') return get30DayRange(timeZoneCd);
    if (normalized === 'thismonth') return getThisMonthRange(timeZoneCd);
    return null;
};

const DATE_TOKEN_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const isSupportedDateFilterValue = (val: any) => {
    if (typeof val !== 'string' || !val) return false;
    if (['Today', 'Yesterday', '7D', /* '30D', */ 'This Month'].includes(val)) return true;
    if (!val.startsWith('custom:')) return false;
    const p = val.split(':');
    if (p.length !== 3) return false;
    const from = p[1];
    const to = p[2];
    return DATE_TOKEN_REGEX.test(from) && DATE_TOKEN_REGEX.test(to) && from <= to;
};

// Human-readable active date range for the "All outlets" export file header —
// preset names (e.g. "Today", "Last 7 Days") pass through as-is; "custom:start:end" formats nicely.
// The export header's date range comes from `exportPeriodFor` in utils/reportPeriod
// (DPFIX-07). The local copy that lived here returned a label only, never the year, and
// was one of seven that had already drifted apart — see DPFIX-05, where Snapshot's copy
// still tested the deleted 'Custom' token and leaked the raw one into a file.

// Cache report payloads so rapid desktop/mobile view switches don't refetch
// for the same location + date range. Entries expire after CACHE_TTL_MS so
// revisiting a date filter after switching away fetches fresh data.
const CACHE_TTL_MS = 60_000;
const productDatasetCache = new Map<string, { value: any; fetchedAt: number }>();
const channelDatasetCache = new Map<string, { value: any; fetchedAt: number }>();
const cacheGet = (map: Map<string, { value: any; fetchedAt: number }>, key: string) => {
    const entry = map.get(key);
    if (!entry) return undefined;
    if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
        map.delete(key);
        return undefined;
    }
    return entry.value;
};
const cacheSet = (map: Map<string, { value: any; fetchedAt: number }>, key: string, value: any) => {
    map.set(key, { value, fetchedAt: Date.now() });
};
const cacheHas = (map: Map<string, { value: any; fetchedAt: number }>, key: string) => cacheGet(map, key) !== undefined;
const getProductCacheKey = (locationId: any, startDate: any, endDate: any, page: any = 0) => `${locationId}__${startDate}__${endDate}__p${page}`;
const getChannelCacheKey = (locationId: any, startDate: any, endDate: any, page: any, size: any) => `${locationId}__${startDate}__${endDate}__${page}__${size}`;

// Normalize API list: response may be { content: [] } or direct array
const normList = (r: any) => (Array.isArray(r) ? r : r?.content ?? r?.data ?? []);
// Server-driven pagination for the item table (category-item-sales): trust the API's
// totalItems / totalPages rather than recomputing from page size (the API may paginate
// by category, so ceil(totalItems / pageSize) does not match totalPages).
const readTableTotalItems = (res: any, rows: any[]) => {
    const n = Number(res?.totalElements ?? res?.totalItems ?? res?.total);
    return Number.isFinite(n) && n >= 0 ? n : rows.length;
};
const readTableTotalPages = (res: any) => {
    const p = Number(res?.totalPages);
    return Number.isFinite(p) && p > 0 ? p : 1;
};
const parseNumber = (value: any) => {
    if (value == null) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const cleaned = trimmed.replace(/[^0-9.-]/g, '');
    if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
};
const pickNumDeep = (obj: any, keys: any) => {
    if (!obj || typeof obj !== 'object') return 0;
    for (const key of keys) {
        const value = obj[key];
        const parsed = parseNumber(value);
        if (parsed != null) return parsed;
    }
    for (const value of Object.values(obj)) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            const nested = pickNumDeep(value, keys);
            if (nested != null) return nested;
        }
    }
    return null;
};
const getCategoryItemSalesTotals = (response: any, rows: any) => {
    const overallGross = pickNumDeep(response, [
        'overallGross',
        'overallGrossSales',
        'totalGross',
        'totalGrossSales',
        'overallRevenue',
        'totalRevenue',
        'overallTotalSales',
        'grandTotalSales',
        'grandTotal',
    ]);
    const overallQty = pickNumDeep(response, [
        'overallQty',
        'overallQuantity',
        'totalQty',
        'totalQuantity',
        'overallItemCount',
        'totalItemCount',
        'grandTotalQty',
        'grandTotalQuantity',
        'totalSalesQty',
        'grandItemCount',
    ]);
    return {
        gross: overallGross != null ? overallGross : rows.reduce((sum, row) => sum + row.revenue, 0),
        qty: overallQty != null ? overallQty : rows.reduce((sum, row) => sum + row.qty, 0),
    };
};
const getCategoryItemSalesRows = (response: any) => {
    if (!response || typeof response !== 'object') return [];
    const directList = normList(response);
    if (Array.isArray(directList) && directList.length > 0) return directList;

    if (Array.isArray(response.items) && response.items.length > 0) return response.items;

    if (Array.isArray(response.categories)) {
        return response.categories.flatMap((category) => {
            const categoryName = category?.categoryName ?? category?.name ?? '';
            const items = Array.isArray(category?.items) ? category.items : [];
            return items.map((item) => ({
                ...item,
                categoryName: item?.categoryName ?? categoryName,
            }));
        });
    }
    return [];
};
const mapCategoryItemSalesRow = (row: any) => ({
    name: row.itemName ?? row.name ?? row.menuItemName ?? row.label ?? '',
    // Keep this aligned with maghil-portal Category & Item Sales mapping.
    revenue: pickNumDeep(row, ['grossSales', 'gross_sales', 'gross', 'itemTotal', 'totalItemPrice', 'totalSales', 'amount', 'revenue']) ?? 0,
    qty: pickNumDeep(row, ['salesQty', 'sales_qty', 'itemCount', 'quantitySold', 'qty', 'quantity', 'count']) ?? 0,
    category: row.categoryName ?? row.category ?? row.productCategory ?? '',
    pmix: Number(row.percentOfTotal ?? 0),
    addonRev: 0,
    refundQty: 0,
});
// Map popular-revenue item to { name, revenue, qty }
const mapPopularRevenue = (row: any) => ({
    name: row.itemName ?? row.name ?? '',
    // Use deep numeric parsing to support values like "1,23,456.78".
    revenue: pickNumDeep(row, ['totalItemPrice', 'revenue', 'grossSales', 'totalSales', 'amount']) ?? 0,
    qty: pickNumDeep(row, ['itemCount', 'qty', 'salesQty', 'quantity', 'count']) ?? 0,
});
// Map category/revenue stream to { name, value, pct }
const mapCategoryRow = (row: any, total: any) => {
    const value = pickNumDeep(row, ['totalSales', 'value', 'revenue', 'amount']) ?? 0;
    return { name: row.productName ?? row.categoryName ?? row.name ?? '', value, pct: total ? (value / total) * 100 : 0 };
};
// Map product/insights/v2/tags (revenue class) response to
// { name, value, pct, units, avgPerItem } for the Top Revenue Streams donut/legend.
// The API now returns sharePercent/itemsSold/avgPerItem directly; total is kept as a
// fallback for callers (e.g. export refetch) that still need to derive pct client-side.
const mapRevenueStreamRow = (row: any, total: any) => {
    const value = pickNumDeep(row, ['totalSales', 'total_sales', 'value', 'revenue', 'amount']) ?? 0;
    const name = row.revenueClass ?? row.revenue_class ?? row.name ?? '';
    const units = pickNumDeep(row, ['itemsSold', 'items_sold', 'unitsSold', 'qty', 'quantity']);
    const pct = pickNumDeep(row, ['sharePercent', 'share_percent']) ?? (total ? (value / total) * 100 : 0);
    const avgFromApi = pickNumDeep(row, ['avgPerItem', 'avg_per_item']);
    const avgPerItem = avgFromApi != null ? avgFromApi : (units != null && units > 0 ? value / units : null);
    // Units represent item counts — round for display so the table doesn't render
    // long floating-point tails (e.g. "130532.8948u") that overflow their column.
    return { name, value, pct, units: units != null ? Math.round(units) : null, avgPerItem };
};
// Sort revenue streams highest-to-lowest by revenue (doc requirement: rows ordered by revenue desc)
const sortRevenueStreams = (rows: any[]) => [...rows].sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

const ITEM_TABLE_PAGE_SIZE_CONST = 10;
// Product Mix (PMIX) renders the top N items by mix %. It must NOT be capped by the
// item table's page size (10), so it has its own larger fetch.
const PMIX_TOP_N = 15;
// Sortable field names for the Full Item Performance table. Desktop columns carry
// the same field via `sortKey`; mobile renders these as sort chips. Clicking the
// same field toggles direction; a new field starts ascending. Category / PMIX % are
// NOT sortable (neither server- nor client-side).
const ITEM_TABLE_SORT_OPTIONS = [
    { field: 'itemName', label: 'Item' },
    { field: 'salesQty', label: 'Qty' },
    { field: 'grossSales', label: 'Revenue' },
];
// Item / Qty Sold / Revenue columns sort CLIENT-SIDE: on click we fetch the whole
// current scope once (size = totalItems) and sort it in memory, so paging through
// the sorted set afterwards needs no more requests.
const FE_SORT_FIELDS = new Set(['itemName', 'salesQty', 'grossSales']);
const FE_SORT_ROW_KEY: Record<string, 'name' | 'qty' | 'revenue'> = {
    itemName: 'name',
    salesQty: 'qty',
    grossSales: 'revenue',
};
const compareFeSortRows = (a: any, b: any, key: string, asc: boolean) => {
    const av = a[key];
    const bv = b[key];
    if (typeof av === 'string' || typeof bv === 'string') {
        const as = String(av ?? '').toLowerCase();
        const bs = String(bv ?? '').toLowerCase();
        return asc ? as.localeCompare(bs) : bs.localeCompare(as);
    }
    const an = Number(av) || 0;
    const bn = Number(bv) || 0;
    return asc ? an - bn : bn - an;
};
const fetchProductMenuDataset = async (opts, page = 0, size = ITEM_TABLE_PAGE_SIZE_CONST) => {
    const [top, bottom, revenueStreamsRaw, cancelled, cancelledReasons, totalsRevenue, tablePayload, pmixRows] = await Promise.all([
        getPopularRevenueTop(opts).then((r) => normList(r).map(mapPopularRevenue)),
        getPopularRevenueBottom(opts).then((r) => normList(r).map(mapPopularRevenue)),
        getSalesByRevenueClass(opts).then((r) => {
            const list = normList(r);
            const total = list.reduce((s, row) => s + (pickNumDeep(row, ['totalSales', 'total_sales', 'value', 'revenue', 'amount']) ?? 0), 0);
            return sortRevenueStreams(list.map((row) => mapRevenueStreamRow(row, total)));
        }),
        getProductCancelledItems(opts).then((r) => normList(r)),
        getProductItemsCancelledReasons(opts).then((r) => normList(r)),
        // Stamp KPIs (Total Item Revenue, Total Items Sold, Avg Revenue / Item)
        getProductTotalsRevenue(opts).then((r) => ({
            revenue: pickNumDeep(r, ['totalItemRevenue', 'total_item_revenue']),
            qty: pickNumDeep(r, ['totalItemsSold', 'total_items_sold']),
            avgPerItem: pickNumDeep(r, ['averageRevenuePerItem', 'average_revenue_per_item']),
        })),
        getProductCategoryItemSales({ ...opts, page, size }).then((res) => {
            console.log('[ProductMenuPerformance] category-item-sales RAW response:', res);
            const list = getCategoryItemSalesRows(res);
            const rows = list.map(mapCategoryItemSalesRow);
            console.log('[ProductMenuPerformance] category-item-sales mapped rows:', rows);
            const totals = getCategoryItemSalesTotals(res, rows);
            const totalElements = readTableTotalItems(res, rows);
            const totalPages = readTableTotalPages(res);
            console.log('[ProductMenuPerformance] category-item-sales totals:', totals, 'totalElements:', totalElements, 'totalPages:', totalPages);
            return { rows, totalElements, totalPages, ...totals };
        }),
        // Dedicated PMIX fetch so the chart can show up to PMIX_TOP_N items regardless of table paging
        getProductCategoryItemSales({ ...opts, page: 0, size: PMIX_TOP_N }).then((res) => getCategoryItemSalesRows(res).map(mapCategoryItemSalesRow)),
    ]);

    const tableRows = tablePayload.rows ?? [];
    const totalRev = tableRows.reduce((s, r) => s + r.revenue, 0);
    // Group all reasons per item; collect all distinct reasons and sum total qty
    const reasonByItem = {};
    (cancelledReasons || []).forEach((row) => {
        const name = row.itemName ?? row.name;
        const count = Number(row.voidedReasonCount ?? row.count ?? 0);
        const reason = row.voidedReason ?? '';
        if (!reasonByItem[name]) reasonByItem[name] = { name, qty: 0, reasons: [] };
        reasonByItem[name].qty += count;
        if (reason && !reasonByItem[name].reasons.includes(reason)) {
            reasonByItem[name].reasons.push(reason);
        }
    });

    const refundData = (cancelled || []).map((row) => {
        const name = row.itemName ?? row.name ?? '';
        const qty = Number(row.itemCount ?? row.qty ?? 0);
        const soldRow = tableRows.find((r) => (r.name || '').trim().toLowerCase() === (name || '').trim().toLowerCase());
        const totalQty = soldRow ? Number(soldRow.qty) : 0;
        const rate = totalQty > 0 ? Math.min(100, (qty / totalQty) * 100) : 0;
        return { name, qty, rate, totalQty, reasons: reasonByItem[name]?.reasons ?? [] };
    }).filter((r) => r.name && r.totalQty > 0);

    const fullTableData = tableRows.map((r) => ({
        ...r,
        refundQty: (cancelled || []).find((c) => (c.itemName ?? c.name) === r.name)?.itemCount ?? (reasonByItem[r.name]?.qty ?? 0),
    }));

    return {
        topItemsRevenue: top,
        bottomItemsRevenue: bottom,
        categoryData: revenueStreamsRaw,
        categoryItemOverall: {
            gross: totalsRevenue.revenue ?? tablePayload.gross ?? null,
            qty: totalsRevenue.qty ?? tablePayload.qty ?? null,
            avgPerItem: totalsRevenue.avgPerItem ?? null,
        },
        pmixData: (() => {
            // Aggregate duplicate item names (same item across multiple categories)
            const agg: Record<string, number> = {};
            (pmixRows || []).forEach((r) => {
                const name = r.name || '';
                agg[name] = (agg[name] || 0) + (Number(r.pmix) || 0);
            });
            const sorted = Object.entries(agg)
                .map(([name, pct]) => ({ name, pct }))
                .filter((r) => r.name) // keep all named items, including 0% mix
                .sort((a, b) => b.pct - a.pct)
                .slice(0, PMIX_TOP_N);
            // Normalize to exactly 100% to eliminate floating-point rounding drift
            const total = sorted.reduce((s, r) => s + r.pct, 0);
            return total > 0
                ? sorted.map((r) => ({ name: r.name, pct: Math.round((r.pct / total) * 10000) / 100 }))
                : sorted;
        })(),
        refundData,
        fullTableData,
        itemTableTotal: tablePayload.totalElements ?? tableRows.length,
        itemTablePages: tablePayload.totalPages ?? 1,
    };
};
const mapCategoryChannelSummaryRows = (response: any) => {
    const list = normList(response);
    const grouped = new Map();
    const labelByKey = new Map();
    const getAmount = (obj: any) => pickNumDeep(obj, ['totalAmount', 'totalSales', 'totalPrice', 'sales', 'amount', 'revenue', 'grossSales', 'itemTotal']) ?? 0;
    const fallbackChannels = ['dinein', 'delivery', 'doordash', 'ubereats', 'pickup', 'instore', 'grubhub', 'swiggy', 'zomato', 'seamless', 'gloriafood', 'directonline'];
    const addAmount = (itemName: any, channelName: any, amount: any) => {
        if (!channelName) return;
        const key = normalizeChannelKey(channelName);
        if (!key) return;
        const parsed = parseNumber(amount);
        const value = parsed != null ? parsed : 0;
        const safeItemName = String(itemName ?? '').trim() || '—';
        if (!grouped.has(safeItemName)) grouped.set(safeItemName, { name: safeItemName, __total: 0 });
        const row = grouped.get(safeItemName);
        row[key] = (Number(row[key] ?? 0) || 0) + value;
        row.__total += value;
        if (!labelByKey.has(key)) labelByKey.set(key, String(channelName));
    };
    list.forEach((row) => {
        const itemName = row?.itemName ?? row?.name ?? row?.menuItemName ?? row?.productName ?? row?.categoryName ?? row?.label ?? '';
        if (Array.isArray(row?.channels) && row.channels.length > 0) {
            row.channels.forEach((ch) => addAmount(itemName, ch?.channelName ?? ch?.channel ?? ch?.name, getAmount(ch)));
        } else {
            const directChannelName = row?.channelName ?? row?.channel ?? row?.typeName ?? row?.sourceName;
            if (directChannelName) {
                addAmount(itemName, directChannelName, getAmount(row));
            } else {
                fallbackChannels.forEach((channelKey) => {
                    const amount = row?.[channelKey] ?? row?.[channelKey.toUpperCase()] ?? row?.[channelKey.charAt(0).toUpperCase() + channelKey.slice(1)];
                    if (amount != null) addAmount(itemName, channelKey, amount);
                });
            }
        }
    });
    const rows = Array.from(grouped.values())
        .sort((a, b) => (b.__total ?? 0) - (a.__total ?? 0))
        .map((row) => {
            const { __total, ...rest } = row;
            return rest;
        });
    const series = Array.from(labelByKey.entries()).map(([key, label], idx) => ({
        key,
        label: formatChannelLabel(label),
        color: getChannelColor(key, idx),
    }));
    return {
        rows,
        series,
        totalPages: Number(response?.totalPages ?? 1) || 1,
        totalElements: Number(response?.totalElements ?? rows.length) || rows.length,
        pageSize: Number(response?.pageable?.pageSize ?? rows.length) || rows.length,
    };
};

// ─── Formatters ──────────────────────────────────────────────────────────────
const fmt = (n: any) => (n >= 1000 ? `${getCurrencySymbol()}${(n / 1000).toFixed(1)}K` : `${getCurrencySymbol()}${n}`);
const fmtFull = (n: any) => `${getCurrencySymbol()}${n.toLocaleString()}`;
const fmtNum = (n: any) => n.toLocaleString();
const fmtQty = (n: any) => {
    const num = Number(n);
    if (!isFinite(num)) return String(n ?? '');
    const str = String(num);
    const dotIdx = str.indexOf('.');
    if (dotIdx === -1) return num.toLocaleString();
    const decimals = str.length - dotIdx - 1;
    if (decimals === 2) {
        return num.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    }
    return num.toLocaleString();
};

// ─── Shared Styles ───────────────────────────────────────────────────────────
const cardStyle = {
    background: T.card,
    borderRadius: 16,
    border: `1px solid ${T.cardBorder}`,
    boxShadow: T.shadow,
    padding: 22,
    marginBottom: 0,
};
const cardTitleStyle = { margin: 0, marginBottom: 4 };
const cardSubtitleStyle = { margin: 0, marginBottom: 16 };

function KPICard({ label, value, isCurrency = true, tooltip }: any) {
    return (
        <div className="rpt-tile" style={{ ...cardStyle, flex: 1, minWidth: 180 }}>
            <div className="rpt-tile__label" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 2 }}>{label}{tooltip && <KpiTooltip text={tooltip} />}</div>
            <div className="rpt-tile__value">
                {isCurrency ? fmtFull(value) : fmtNum(value)}
            </div>
        </div>
    );
}

function FilterPill({ label, active, onClick }: any) {
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                padding: '4px 12px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                fontSize: 11.5,
                fontWeight: 500,
                fontFamily: T.font,
                transition: 'all 0.15s',
                background: active ? T.accentSoft : 'transparent',
                color: active ? T.accent : T.t2,
            }}
        >
            {label}
        </button>
    );
}

function hexToRgba(hex: string, alpha: number): string {
    const clean = hex.replace('#', '');
    const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

function HorizontalBarChart({ data, valueKey, labelKey = 'name', color = T.accent, maxVal, formatValue = fmtFull }: any) {
    const max = maxVal || Math.max(...data.map((d) => d[valueKey]));
    const n = data.length;
    const useRankedColors = n >= 8;
    const sortedByVal = useRankedColors
        ? [...data].map((d: any, i: number) => ({ i, v: Number(d[valueKey]) || 0 })).sort((a, b) => b.v - a.v)
        : [];
    const rank1Idx = sortedByVal[0]?.i ?? -1;
    const rank2Idx = sortedByVal[1]?.i ?? -1;
    const rank3Idx = sortedByVal[2]?.i ?? -1;
    const getFillBg = (i: number) => {
        if (!useRankedColors) return color;
        if (i === rank1Idx) return color;
        if (i === rank2Idx) return hexToRgba(color, 0.75);
        if (i === rank3Idx) return hexToRgba(color, 0.55);
        return hexToRgba(color, 0.40);
    };
    return (
        <div className="rpt-flex-col rpt-gap-10">
            {data.map((item, i) => (
                <div key={i} className="rpt-flex-row rpt-flex-center rpt-gap-12">
                    <div style={{ width: 160, fontSize: 12, fontWeight: 500, color: T.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{item[labelKey]}</div>
                    <div style={{ flex: 1, background: T.hover, borderRadius: 3, height: 6, overflow: 'hidden' }}>
                        <div style={{ width: `${(item[valueKey] / max) * 100}%`, height: '100%', background: getFillBg(i), borderRadius: 3, transition: 'width 0.6s ease' }} />
                    </div>
                    <div style={{ width: 70, fontSize: 12, fontWeight: 600, color: T.t1, textAlign: 'right', flexShrink: 0 }}>{formatValue(item[valueKey])}</div>
                </div>
            ))}
        </div>
    );
}

function DataTable({ columns, data, sortBy, sortAsc, onSort }: any) {
    // Server-driven sort when `onSort` is supplied: the parent owns sortBy/sortAsc
    // and refetches; the table renders rows as-is. Otherwise fall back to local sort.
    const serverSort = typeof onSort === 'function';
    const [sortCol, setSortCol] = useState<any>(null);
    const [sortDir, setSortDir] = useState('desc');
    const sorted = useMemo(() => {
        if (serverSort || !sortCol) return data;
        return [...data].sort((a, b) => (sortDir === 'desc' ? b[sortCol] - a[sortCol] : a[sortCol] - b[sortCol]));
    }, [data, sortCol, sortDir, serverSort]);

    const handleSort = (col: any) => {
        if (serverSort) { onSort(col.sortKey || col.key); return; }
        const key = col.key;
        if (sortCol === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        else { setSortCol(key); setSortDir('desc'); }
    };
    const arrowFor = (col: any) => {
        if (serverSort) {
            const field = col.sortKey || col.key;
            return sortBy === field ? (sortAsc ? '↑' : '↓') : '';
        }
        return sortCol === col.key ? (sortDir === 'desc' ? '↓' : '↑') : '';
    };

    const rows = serverSort ? data : sorted;
    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: T.font }}>
                <thead>
                    <tr>
                        {columns.map((col) => (
                            <th
                                key={col.key}
                                onClick={() => col.sortable && handleSort(col)}
                                style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: T.t3,
                                    textTransform: 'uppercase',
                                    textAlign: col.align || 'left',
                                    padding: '10px 12px',
                                    borderBottom: `1px solid ${T.divider}`,
                                    cursor: col.sortable ? 'pointer' : 'default',
                                    userSelect: 'none',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {col.label} {arrowFor(col)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr
                            key={i}
                            style={{ borderBottom: `1px solid ${T.divider}` }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                            {columns.map((col) => (
                                <td
                                    key={col.key}
                                    style={{
                                        fontSize: 12,
                                        fontWeight: col.bold ? 500 : 400,
                                        color: col.color || T.t1,
                                        textAlign: col.align || 'left',
                                        padding: '10px 12px',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {col.render ? col.render(row[col.key], row) : row[col.key]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function SearchableMultiSelect({ label, options, selected, onChange, placeholder = 'Search...', menuAlign = 'left' }: any) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const filtered = options.filter((o) => o.toLowerCase().includes(search.toLowerCase()));
    const toggle = (val: any) => {
        onChange(selected.includes(val) ? selected.filter((s) => s !== val) : [...selected, val]);
    };
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);
    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <div
                onClick={() => setOpen(!open)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '0 10px',
                    height: 34,
                    boxSizing: 'border-box',
                    borderRadius: 8,
                    border: `1px solid ${selected.length > 0 ? T.accent : T.cardBorder}`,
                    background: selected.length > 0 ? T.accentSoft : T.surface,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 500,
                    color: selected.length > 0 ? T.accent : T.t2,
                    minWidth: 120,
                    userSelect: 'none',
                }}
            >
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selected.length === 0 ? label : selected.length === 1 ? selected[0] : `${selected.length} selected`}
                </span>
                <span style={{ fontSize: 8, color: T.t3 }}>▼</span>
            </div>
            {open && (
                <div
                    style={{
                        position: 'absolute',
                            top: 'calc(100% + 4px)',
                            ...(menuAlign === 'right' ? { right: 0 } : { left: 0 }),
                            zIndex: 50,
                            minWidth: 220,
                            background: T.surface,
                            border: `1px solid ${T.cardBorder}`,
                            borderRadius: 10,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                            overflow: 'hidden',
                        }}
                    >
                        <div style={{ padding: '8px 10px', borderBottom: `1px solid ${T.divider}` }}>
                            <input
                                autoFocus
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={placeholder}
                                style={{ width: '100%', border: 'none', outline: 'none', fontSize: 12, fontFamily: T.font, color: T.t1, background: 'transparent', padding: 0 }}
                            />
                        </div>
                        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                            {filtered.length === 0 && <div style={{ padding: '10px 12px', fontSize: 11, color: T.t3 }}>No results</div>}
                            {filtered.map((opt) => {
                                const isSelected = selected.includes(opt);
                                return (
                                    <div
                                        key={opt}
                                        onClick={() => toggle(opt)}
                                        style={{
                                            padding: '7px 12px',
                                            fontSize: 12,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            background: isSelected ? T.accentSoft : 'transparent',
                                            color: isSelected ? T.accent : T.t1,
                                            fontWeight: isSelected ? 600 : 400,
                                        }}
                                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = T.hover; }}
                                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                                    >
                                        <span
                                            style={{
                                                width: 14,
                                                height: 14,
                                                borderRadius: 3,
                                                border: `1.5px solid ${isSelected ? T.accent : T.cardBorder}`,
                                                background: isSelected ? T.accent : 'transparent',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 9,
                                                color: '#fff',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {isSelected ? '✓' : ''}
                                        </span>
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt}</span>
                                    </div>
                                );
                            })}
                        </div>
                        {selected.length > 0 && (
                            <div style={{ padding: '6px 10px', borderTop: `1px solid ${T.divider}`, display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={() => { onChange([]); setSearch(''); }}
                                    style={{ border: 'none', background: 'none', fontSize: 11, color: T.accent, cursor: 'pointer', fontFamily: T.font, fontWeight: 500, padding: '2px 4px' }}
                                >
                                    Clear all
                                </button>
                            </div>
                        )}
                </div>
            )}
        </div>
    );
}

// Windowed list of page numbers (with ellipsis) for the numbered pagination control.
// `compact` (mobile) shows a tighter window so the control never overflows narrow screens.
function getPageItems(current: number, total: number, compact = false): (number | 'dots')[] {
    const threshold = compact ? 5 : 7;
    if (total <= threshold) return Array.from({ length: total }, (_, i) => i + 1);
    const items: (number | 'dots')[] = [1];
    const span = compact ? 0 : 1;
    const left = Math.max(2, current - span);
    const right = Math.min(total - 1, current + span);
    if (left > 2) items.push('dots');
    for (let i = left; i <= right; i++) items.push(i);
    if (right < total - 1) items.push('dots');
    items.push(total);
    return items;
}

// Numbered pagination: "range" on the left, ‹ [1] [2] … › on the right (active page filled).
function ItemTablePagination({ page, totalPages, onChange, rangeLabel, compact = false }: any) {
    const cell = compact ? 26 : 30;
    const fs = compact ? 11 : 12;
    const base: any = {
        minWidth: cell, height: cell, padding: '0 6px', borderRadius: 6, fontSize: fs, fontWeight: 600,
        fontFamily: T.font, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
    };
    const navStyle = (disabled: boolean) => ({
        ...base, border: `1px solid ${T.cardBorder}`, background: T.surface, color: T.t2,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1,
    });
    return (
        <div
            className="rpt-flex-row rpt-flex-center rpt-flex-between rpt-flex-wrap rpt-gap-8"
            style={{ marginTop: compact ? 10 : 12, paddingTop: compact ? 10 : 12, borderTop: `1px solid ${T.divider}` }}
        >
            <span style={{ fontSize: compact ? 10 : 11, color: T.t3, whiteSpace: 'nowrap' }}>{rangeLabel}</span>
            <div className="rpt-flex-row rpt-flex-center rpt-gap-6">
                <button type="button" disabled={page <= 1} onClick={() => onChange(Math.max(1, page - 1))} style={navStyle(page <= 1)} aria-label="Previous page">‹</button>
                {getPageItems(page, totalPages, compact).map((it, i) => it === 'dots' ? (
                    <span key={`dots-${i}`} style={{ fontSize: fs, color: T.t3, padding: '0 2px' }}>…</span>
                ) : (
                    <button
                        key={it}
                        type="button"
                        onClick={() => onChange(it)}
                        style={{
                            ...base,
                            border: `1px solid ${it === page ? T.accent : T.cardBorder}`,
                            background: it === page ? T.accent : T.surface,
                            color: it === page ? '#fff' : T.t2,
                            cursor: 'pointer',
                        }}
                    >{it}</button>
                ))}
                <button type="button" disabled={page >= totalPages} onClick={() => onChange(Math.min(totalPages, page + 1))} style={navStyle(page >= totalPages)} aria-label="Next page">›</button>
            </div>
        </div>
    );
}

const DonutTooltip = ({ active, payload }: any) => {
    if (active && payload?.length) {
        const row = payload[0].payload ?? {};
        const pct = row.pct;
        const pctStr = pct != null ? `${Number(pct).toFixed(2)}%` : '';
        const unitsStr = row.units != null ? Number(row.units).toLocaleString() : '—';
        const avgStr = row.avgPerItem != null ? `${getCurrencySymbol()}${Number(row.avgPerItem).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
        return (
            <div style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: '8px 12px', boxShadow: T.shadow, fontFamily: T.font, minWidth: 150 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{payload[0].name}</div>
                <div style={{ fontSize: 11, color: T.t2, marginTop: 2 }}>{getCurrencySymbol()}{Number(payload[0].value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {pctStr && `(${pctStr})`}</div>
                <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>Units: {unitsStr} · Avg/item: {avgStr}</div>
            </div>
        );
    }
    return null;
};

const BarTooltip = ({ active, payload, label }: any) => {
    if (active && payload?.length) {
        return (
            <div style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: '8px 12px', boxShadow: T.shadow, fontFamily: T.font }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, marginBottom: 4 }}>{label}</div>
                {payload.map((p, i) => (
                    <div key={i} style={{ fontSize: 11, color: T.t2, display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color, display: 'inline-block' }} />
                        {p.name}: {fmtFull(p.value)}
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

function ItemDetailPanel({ item, onClose, addonData = [], refundData = [], channelItems = [], pmixData = [] }: any) {
    if (!item) return null;
    const addon = addonData.find((a) => a.name === item.name);
    const refund = refundData.find((r) => r.name === item.name);
    const channel = channelItems.find((c) => c.name === item.name);

    // ── Item Detail Panel data sources ──────────────────────────────────────
    // KPI row (Revenue, Qty, PMIX) ← /product/insights/category-item-sales
    console.log('[ItemDetailPanel] KPI row — source: /product/insights/category-item-sales', {
        name: item.name, revenue: item.revenue, qty: item.qty, category: item.category,
    });
    // Channel Split ← /sales/category/getCategoryChannelSummary
    console.log('[ItemDetailPanel] Channel split — source: /sales/category/getCategoryChannelSummary', channel ?? 'no match for this item');
    // Refund & Void ← /product/insights/cancelled-items + /product/insights/items-cancelled-reason
    console.log('[ItemDetailPanel] Refund/Void — source: /product/insights/cancelled-items + /product/insights/items-cancelled-reason', refund ?? 'no refund record for this item');
    // ────────────────────────────────────────────────────────────────────────
    const channelBreakdown = channel
        ? Object.entries(channel)
            .filter(([k, v]) => k !== 'name' && Number(v) > 0)
            .map(([k, v]) => ({ key: k, label: formatChannelLabel(k), val: Number(v) }))
            .sort((a, b) => b.val - a.val)
        : [];
    const channelTotal = channelBreakdown.reduce((sum, c) => sum + c.val, 0);

    return (
        <div style={{ position: 'fixed', top: 0, right: 0, width: 420, height: '100vh', background: T.surface, boxShadow: '-4px 0 20px rgba(0,0,0,0.08)', zIndex: 100, overflowY: 'auto', fontFamily: T.font, paddingTop: 'env(safe-area-inset-top)' }}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <div style={{ fontSize: 11, color: T.t3, marginBottom: 4 }}>Item Detail</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: T.t1 }}>{item.name}</div>
                </div>
                <button type="button" onClick={onClose} style={{ background: T.hover, border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 16, color: T.t2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    {[
                        { label: 'Revenue', value: fmtFull(item.revenue) },
                        { label: 'Qty Sold', value: fmtNum(item.qty) },
                        { label: 'PMIX', value: `${(pmixData.find((p) => p.name === item.name)?.pct || 0).toFixed(2)}%` },
                    ].map((k, i) => (
                        <div key={i} style={{ background: T.bg, borderRadius: 10, padding: '12px 10px', textAlign: 'center' }}>
                            <div style={{ fontSize: 8, fontWeight: 400, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>{k.label}</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: T.t1 }}>{k.value}</div>
                        </div>
                    ))}
                </div>
                {addon && (
                    <div style={{ ...cardStyle, padding: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginBottom: 10 }}>Add-On Performance</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.t2, marginBottom: 6 }}>
                            <span>Add-On Revenue</span>
                            <span style={{ fontWeight: 600, color: T.t1 }}>{fmtFull(addon.addon)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.t2 }}>
                            <span>Attach Rate</span>
                            <span style={{ fontWeight: 600, color: T.accent }}>{addon.rate}%</span>
                        </div>
                    </div>
                )}
                {channel && (
                    <div style={{ ...cardStyle, padding: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginBottom: 12 }}>Channel Split</div>
                        {channelBreakdown.map((ch, i) => (
                            <div key={i} style={{ marginBottom: 8 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
                                    <span style={{ color: T.t2 }}>{ch.label}</span>
                                    <span style={{ fontWeight: 600, color: T.t1 }}>{fmtFull(ch.val)} ({((ch.val / channelTotal) * 100).toFixed(2)}%)</span>
                                </div>
                                <div style={{ background: T.hover, borderRadius: 3, height: 5 }}>
                                    <div style={{ width: `${(ch.val / channelTotal) * 100}%`, height: '100%', background: getChannelColor(ch.key, i), borderRadius: 3, opacity: 0.7 }} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {refund ? (
                    <div style={{ ...cardStyle, padding: 16, border: '1px solid rgba(220,38,38,0.2)' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.red, marginBottom: 10 }}>Refund & Void Activity</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.t2, marginBottom: 4 }}>
                            <span>Refund Rate</span>
                            <span style={{ fontWeight: 600, color: T.red }}>{refund.rate}%</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.t2, marginBottom: 4 }}>
                            <span>Refunded Qty</span>
                            <span style={{ fontWeight: 600, color: T.t1 }}>{refund.qty}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.t2 }}>
                            <span>Reasons</span>
                            <span style={{ fontWeight: 500, color: T.orange, textAlign: 'right' }}>{(refund.reasons && refund.reasons.length > 0 ? refund.reasons : ['—']).join(', ')}</span>
                        </div>
                    </div>
                ) : (
                    <div style={{ ...cardStyle, padding: 16, background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.15)' }}>
                        <div style={{ fontSize: 12, color: T.green, fontWeight: 500 }}>No refunds or voids recorded for this item.</div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MODIFIER PERFORMANCE TABLE
// Data comes from GET /product/insights/v2/modifier-analysis (getModifierAnalysis).
// ═══════════════════════════════════════════════════════════════════════════════
interface ModifierRow {
    id: string;
    groupName: string;
    modifierName: string;
    attachRate: number | null;
    qty: number;
    revenue: number;
}

interface ModifierGroupRow {
    id: string;
    groupName: string;
    attachRate: number | null;
    qty: number;
    revenue: number;
    modifiers: ModifierRow[];
}

const fmtAttachRate = (v: number | null) => (v == null ? '—' : `${v.toFixed(1)}%`);

// Individual-view API item -> flat row. `modifierName`/`modifierOptionName` hold
// the group / option names respectively for this flat shape.
const mapModifierApiItem = (it: any, idx: number): ModifierRow => ({
    id: it?.modifierOptionId ?? `mod-item-${idx}`,
    groupName: it?.modifierName ?? '',
    modifierName: it?.modifierOptionName ?? '',
    attachRate: it?.attachRate != null ? Number(it.attachRate) : null,
    qty: Number(it?.quantity ?? 0),
    revenue: Number(it?.upchargeRevenue ?? 0),
});

// Grouped-view API group -> group row + nested member rows.
const mapModifierApiGroup = (g: any): ModifierGroupRow => ({
    id: g?.modifierId ?? g?.modifierName ?? '',
    groupName: g?.modifierName ?? '',
    attachRate: g?.attachRate != null ? Number(g.attachRate) : null,
    qty: Number(g?.quantity ?? 0),
    revenue: Number(g?.upchargeRevenue ?? 0),
    modifiers: (Array.isArray(g?.modifiers) ? g.modifiers : []).map((opt: any, i: number) => ({
        id: opt?.modifierOptionId ?? `${g?.modifierId ?? 'grp'}-${i}`,
        groupName: g?.modifierName ?? '',
        modifierName: opt?.modifierOptionName ?? '',
        attachRate: opt?.attachRate != null ? Number(opt.attachRate) : null,
        qty: Number(opt?.quantity ?? 0),
        revenue: Number(opt?.upchargeRevenue ?? 0),
    })),
});

// Shared data-fetching/filtering/sorting state for both the desktop table and mobile card list.
// Group options come from GET /product/insights/v2/modifier-groups, item options from
// GET /product/insights/v2/items.
function useModifierPerformanceTable(locationId: string, token: string | null, startDate: string, endDate: string) {
    const [view, setView] = useState<'grouped' | 'individual'>('grouped');
    const [groupFilter, setGroupFilter] = useState(''); // '' = All groups; otherwise a modifierId
    const [itemFilter, setItemFilter] = useState(''); // '' = All items; otherwise an itemId
    const [showUnpriced, setShowUnpriced] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sortAsc, setSortAsc] = useState(false); // default: Attach Rate descending
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [groupRows, setGroupRows] = useState<ModifierGroupRow[]>([]);
    const [itemRows, setItemRows] = useState<ModifierRow[]>([]);
    const [totalModifiers, setTotalModifiers] = useState(0);
    const [apiTotalPages, setApiTotalPages] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [failure, setFailure] = useState(false);
    const [retryTick, setRetryTick] = useState(0);
    const [modifierGroups, setModifierGroups] = useState<{ modifierId: string; modifierName: string }[]>([]);
    const [modifierItems, setModifierItems] = useState<{ itemId: string; itemName: string }[]>([]);
    const [pageSize, setPageSize] = useState<number | 'All'>(10);
    const [page, setPage] = useState(1); // 1-indexed, matches ItemTablePagination

    // Debounce the search box (350ms) so we're not firing a request per keystroke.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
        return () => clearTimeout(t);
    }, [search]);

    const retry = useCallback(() => setRetryTick((t) => t + 1), []);

    // "All groups" dropdown options — keyed by locationId only (not date-scoped).
    useEffect(() => {
        if (!locationId || !token) return;
        let cancelled = false;
        getModifierGroups({ locationId, token })
            .then((res: any) => {
                if (cancelled) return;
                const list = (Array.isArray(res) ? res : []).map((g: any) => ({
                    modifierId: g?.modifierId ?? '',
                    modifierName: g?.modifierName ?? '',
                })).filter((g) => g.modifierId && g.modifierName);
                setModifierGroups(list);
            })
            .catch(() => { if (!cancelled) setModifierGroups([]); });
        return () => { cancelled = true; };
    }, [locationId, token]);

    // "All items" dropdown options — keyed by locationId only (not date-scoped).
    useEffect(() => {
        if (!locationId || !token) return;
        let cancelled = false;
        getModifierItems({ locationId, token })
            .then((res: any) => {
                if (cancelled) return;
                const list = (Array.isArray(res) ? res : []).map((it: any) => ({
                    itemId: it?.itemId ?? '',
                    itemName: it?.itemName ?? '',
                })).filter((it) => it.itemId && it.itemName);
                setModifierItems(list);
            })
            .catch(() => { if (!cancelled) setModifierItems([]); });
        return () => { cancelled = true; };
    }, [locationId, token]);

    // Drop the selected group/item filter if it no longer exists for this location.
    useEffect(() => {
        if (!groupFilter) return;
        if (!modifierGroups.some((g) => g.modifierId === groupFilter)) setGroupFilter('');
    }, [modifierGroups, groupFilter]);

    useEffect(() => {
        if (!itemFilter) return;
        if (!modifierItems.some((it) => it.itemId === itemFilter)) setItemFilter('');
    }, [modifierItems, itemFilter]);

    const modifierId = groupFilter || undefined;
    const itemId = itemFilter || undefined;
    const sortDir = sortAsc ? 'asc' : 'desc';
    const size = pageSize === 'All' ? MODIFIER_ALL_SIZE : pageSize;

    // Reset to page 1 whenever the active dataset changes shape (view/filters/search/sort/size).
    useEffect(() => { setPage(1); }, [view, pageSize, debouncedSearch, showUnpriced, modifierId, itemId, sortAsc]);

    useEffect(() => {
        if (!locationId || !token || !startDate || !endDate) return;
        let cancelled = false;
        setLoading(true);
        setFailure(false);
        getModifierAnalysis({
            locationId, startDate, endDate, view, modifierId, itemId, search: debouncedSearch || undefined, showUnpriced,
            page, size, sortBy: 'attachRate', sortDir, token,
        })
            .then((res: any) => {
                if (cancelled) return;
                setGroupRows(Array.isArray(res?.groups) ? res.groups.map(mapModifierApiGroup) : []);
                setItemRows(Array.isArray(res?.items) ? res.items.map(mapModifierApiItem) : []);
                // Server field is `totalElements` (fall back to the older `totalModifiers` name).
                setTotalModifiers(Number(res?.totalElements ?? res?.totalModifiers ?? 0));
                setApiTotalPages(res?.totalPages != null ? Number(res.totalPages) : null);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setGroupRows([]);
                setItemRows([]);
                setTotalModifiers(0);
                setApiTotalPages(null);
                setFailure(true);
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [locationId, token, startDate, endDate, view, modifierId, itemId, debouncedSearch, showUnpriced, page, size, sortDir, retryTick]);

    // Nested member rows (inside an expanded group) aren't guaranteed to come back
    // sorted from the server, so sort those client-side; top-level ordering/paging
    // is entirely server-driven (sortBy/sortDir/page/size).
    const cmp = useCallback((a: number | null, b: number | null) => {
        const av = a ?? -1, bv = b ?? -1;
        return sortAsc ? av - bv : bv - av;
    }, [sortAsc]);
    const groupedRows = useMemo(
        () => groupRows.map((g) => ({ ...g, modifiers: [...g.modifiers].sort((a, b) => cmp(a.attachRate, b.attachRate)) })),
        [groupRows, cmp],
    );
    const individualRows = itemRows;

    const activeTotalPages = pageSize === 'All' ? 1 : Math.max(1, apiTotalPages ?? Math.ceil(totalModifiers / pageSize));
    // Clamp page if a filter change shrank the result set below the current page.
    useEffect(() => { if (page > activeTotalPages) setPage(activeTotalPages); }, [page, activeTotalPages]);

    const rangeFrom = totalModifiers === 0 ? 0 : pageSize === 'All' ? 1 : (page - 1) * pageSize + 1;
    const rangeTo = pageSize === 'All' ? totalModifiers : Math.min(page * pageSize, totalModifiers);

    const toggleGroup = useCallback((groupId: string) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
            return next;
        });
    }, []);

    const toggleSort = useCallback(() => setSortAsc((v) => !v), []);

    // Full-dataset fetch for CSV/PDF export — the table itself only holds the current
    // server page, so export re-fetches everything on demand, using whichever view
    // (Grouped/Individual) is currently selected — NOT hardcoded — so the export
    // reflects what's actually on screen. Both shapes flatten to the same
    // Modifier Group / Modifier / Attach Rate / Qty / Upcharge Revenue row shape.
    // `overrideLocationId`, when passed, is a DIFFERENT outlet from an "All outlets"
    // export (see ReportSectionExportButton) — the currently selected group/item
    // filter IDs belong to THIS outlet and are meaningless (or wrong) for another
    // outlet, so they're dropped whenever the override targets a different location.
    const fetchAllForExport = useCallback(async (overrideLocationId?: string): Promise<ModifierRow[]> => {
        const loc = overrideLocationId ?? locationId;
        if (!loc || !token || !startDate || !endDate) return [];
        const isOtherOutlet = !!overrideLocationId && overrideLocationId !== locationId;
        const baseParams = {
            locationId: loc, startDate, endDate, view,
            modifierId: isOtherOutlet ? undefined : modifierId,
            itemId: isOtherOutlet ? undefined : itemId,
            search: debouncedSearch || undefined, showUnpriced,
            sortBy: 'attachRate', sortDir, token,
        };
        // Probe with size:1 to learn the real row count for this scope (rather than
        // sending an arbitrary large size like 100000 on every export), then fetch
        // everything in one call sized exactly to that. In grouped view, totalElements
        // counts top-level GROUPS — fetching that many groups still returns every
        // modifier (each group's nested `modifiers` isn't separately paginated).
        const probe: any = await getModifierAnalysis({ ...baseParams, page: 1, size: 1 });
        const total = Number(probe?.totalElements ?? probe?.totalModifiers ?? 0);
        if (total <= 0) return [];
        const res: any = await getModifierAnalysis({ ...baseParams, page: 1, size: total });
        if (view === 'grouped') {
            return Array.isArray(res?.groups) ? res.groups.map(mapModifierApiGroup).flatMap((g: ModifierGroupRow) => g.modifiers) : [];
        }
        return Array.isArray(res?.items) ? res.items.map(mapModifierApiItem) : [];
    }, [locationId, token, startDate, endDate, view, modifierId, itemId, debouncedSearch, showUnpriced, sortDir]);

    return {
        view, setView,
        groupFilter, setGroupFilter,
        itemFilter, setItemFilter,
        showUnpriced, setShowUnpriced,
        search, setSearch,
        sortAsc, toggleSort,
        expandedGroups, toggleGroup,
        // Current server page — this IS what's rendered (pagination/sorting are server-driven).
        individualRows, groupedRows,
        page, setPage, pageSize, setPageSize, totalPages: activeTotalPages,
        rangeFrom, rangeTo,
        fetchAllForExport,
        totalCount: totalModifiers,
        hasAnyPricedData: totalModifiers > 0,
        loading, failure, retry,
        groupOptions: [...modifierGroups]
            .sort((a, b) => a.modifierName.localeCompare(b.modifierName))
            .map((g) => ({ id: g.modifierId, label: g.modifierName })),
        itemOptions: [...modifierItems]
            .sort((a, b) => a.itemName.localeCompare(b.itemName))
            .map((it) => ({ id: it.itemId, label: it.itemName })),
    };
}

// Custom single-select dropdown (native <select> renders with unstyleable OS
// chrome — this matches the app's own dropdown panel styling, same pattern as
// SearchableMultiSelect / ExportMenu).
function ModifierSingleSelect({ value, allLabel, options, onChange, compact = false }: { value: string; allLabel: string; options: { id: string; label: string }[]; onChange: (v: string) => void; compact?: boolean }) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const isDefault = value === '';
    const displayLabel = isDefault ? allLabel : (options.find((opt) => opt.id === value)?.label ?? allLabel);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div ref={containerRef} style={{ position: 'relative', flex: compact ? 1 : undefined, minWidth: compact ? 0 : 160 }}>
            {/* maxWidth + overflow:hidden here (not on the wrapper, which stays
                unbounded so the open dropdown below isn't clipped): without an
                explicit width this flex row sizes to fit its content, so a long
                selected value grew the closed button instead of the ellipsis
                span ever clipping it. The value span needs flex:1 + minWidth:0
                to actually shrink and truncate inside that now-bounded row. */}
            <div
                onClick={() => setOpen((v) => !v)}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                    height: compact ? 32 : 34, padding: '0 10px', boxSizing: 'border-box',
                    borderRadius: 8, border: `1px solid ${!isDefault ? T.accent : T.cardBorder}`,
                    background: !isDefault ? T.accentSoft : T.surface, cursor: 'pointer',
                    fontSize: compact ? 11 : 12, fontWeight: !isDefault ? 600 : 500,
                    color: !isDefault ? T.accent : T.t1, userSelect: 'none',
                    maxWidth: compact ? 160 : 220, overflow: 'hidden',
                }}
            >
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayLabel}</span>
                <span className="material-symbols-rounded" style={{ fontSize: compact ? 15 : 16, color: !isDefault ? T.accent : T.t3, flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>expand_more</span>
            </div>
            {open && (
                /* maxWidth caps the menu so a long item/group name can't push it off
                   the right edge of the screen; the row wraps that name across
                   lines instead of clipping it (no ellipsis, no nowrap) so it's
                   always fully readable. */
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50, minWidth: '100%', maxWidth: 320, maxHeight: 260, overflowY: 'auto', background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.10)' }}>
                    {[{ id: '', label: allLabel }, ...options].map((opt) => {
                        const isSelected = opt.id === value;
                        return (
                            <div
                                key={opt.id || '__all__'}
                                onClick={() => { onChange(opt.id); setOpen(false); }}
                                style={{
                                    padding: '8px 12px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8,
                                    background: isSelected ? T.accentSoft : 'transparent', color: isSelected ? T.accent : T.t1, fontWeight: isSelected ? 600 : 400,
                                }}
                                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = T.hover; }}
                                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                            >
                                <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}>{opt.label}</span>
                                {isSelected && <span style={{ fontSize: 12, color: T.accent, flexShrink: 0, marginTop: 1 }}>✓</span>}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function ModifierEmptyState({ minHeight = 220 }: { minHeight?: number }) {
    return (
        <div style={{ minHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '20px 12px' }}>
            <div style={{ fontSize: 13, color: T.t3 }}>No modifier activity for the selected period.</div>
        </div>
    );
}

function ModifierViewToggle({ view, onChange, compact = false }: { view: 'grouped' | 'individual'; onChange: (v: 'grouped' | 'individual') => void; compact?: boolean }) {
    const seg = (v: 'grouped' | 'individual', label: string, borderLeft: boolean) => (
        <button
            type="button"
            onClick={() => onChange(v)}
            style={{
                padding: compact ? '5px 9px' : '6px 14px', fontSize: compact ? 11 : 12, fontWeight: 600, fontFamily: T.font, border: 'none',
                borderLeft: borderLeft ? `1px solid ${T.cardBorder}` : 'none',
                background: view === v ? T.accentSoft : 'transparent',
                color: view === v ? T.accent : T.t2,
                cursor: 'pointer', whiteSpace: 'nowrap',
            }}
        >
            {label}
        </button>
    );
    return (
        <div style={{ display: 'flex', border: `1px solid ${T.cardBorder}`, borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
            {seg('grouped', 'Grouped', false)}
            {seg('individual', 'Individual', true)}
        </div>
    );
}

// Custom "Records per page" dropdown — matches the Items Overview page-size control
// (SnapshotScreen) styling: pill button + floating menu opening upward, instead of a
// native <select>.
function ModifierPageSizeSelect({ value, options, onChange, compact = false }: { value: number | 'All'; options: (number | 'All')[]; onChange: (v: number | 'All') => void; compact?: boolean }) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const fs = compact ? 9 : 11;

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div ref={containerRef} style={{ display: 'inline-flex', alignItems: 'center', gap: compact ? 4 : 6, fontSize: fs, color: T.t3, fontFamily: T.font }}>
            {compact ? 'Per page:' : 'Records per page:'}
            <div style={{ position: 'relative' }}>
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: fs, fontWeight: 600, color: T.t1, fontFamily: T.font, padding: compact ? '2px 4px' : '3px 6px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: T.card, cursor: 'pointer', lineHeight: 1 }}
                >
                    {value}
                    <span className="material-symbols-rounded" style={{ fontSize: fs + 2, lineHeight: 1, color: T.t3 }}>expand_more</span>
                </button>
                {open && (
                    <>
                        <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 500 }} />
                        <div style={{ position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 501, overflow: 'hidden', minWidth: '100%' }}>
                            {options.map((s) => (
                                <button
                                    key={String(s)}
                                    type="button"
                                    onClick={() => { onChange(s); setOpen(false); }}
                                    style={{ display: 'block', width: '100%', textAlign: 'center', fontSize: fs, fontWeight: s === value ? 700 : 500, color: s === value ? T.accent : T.t1, fontFamily: T.font, padding: '7px 14px', border: 'none', background: s === value ? `${T.accent}12` : 'transparent', cursor: 'pointer' }}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ─── Desktop: Modifier Performance table ────────────────────────────────────
function ModifierPerformanceCard({ branch, locationId, token, startDate, endDate, exportDateRangeLabel }: any) {
    const m = useModifierPerformanceTable(locationId, token, startDate, endDate);

    return (
        <div style={cardStyle}>
            <div className="rpt-flex-row rpt-flex-between rpt-flex-center" style={{ marginBottom: 12 }}>
                <p className="rpt-section-title" style={{ ...cardTitleStyle, marginBottom: 0 }}>Modifier Performance</p>
                {!m.loading && !m.failure && m.hasAnyPricedData && m.totalCount > 0 && (
                    <ReportSectionExportButton
                        dateRangeLabel={exportDateRangeLabel}
                        variant="desktop"
                        title="Modifier Performance"
                        filename={`product-modifiers-${startDate || 'period'}${endDate && endDate !== startDate ? `-to-${endDate}` : ''}`}
                        buildExport={async (format, opts) => {
                            const rows = await m.fetchAllForExport(opts?.locationId);
                            return {
                                headers: ['Modifier Group', 'Modifier', 'Attach Rate', 'Qty', 'Upcharge Revenue'],
                                rows: rows.map((r) => [
                                    r.groupName,
                                    r.modifierName,
                                    fmtAttachRate(r.attachRate),
                                    r.qty,
                                    formatMoneyForExport(r.revenue),
                                ]),
                            };
                        }}
                    />
                )}
            </div>

            <div className="rpt-flex-row rpt-flex-center rpt-flex-wrap rpt-gap-8" style={{ marginBottom: 12 }}>
                <ModifierViewToggle view={m.view} onChange={m.setView} />
                <ModifierSingleSelect value={m.groupFilter} allLabel="All groups" options={m.groupOptions} onChange={m.setGroupFilter} />
                <ModifierSingleSelect value={m.itemFilter} allLabel="All items" options={m.itemOptions} onChange={m.setItemFilter} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.t2, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={m.showUnpriced} onChange={(e) => m.setShowUnpriced(e.target.checked)} style={{ width: 14, height: 14, accentColor: T.accent, cursor: 'pointer' }} />
                    Show unpriced
                </label>
                <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
                    <span className="material-symbols-rounded" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: T.t3, pointerEvents: 'none' }}>search</span>
                    <input
                        type="text"
                        value={m.search}
                        onChange={(e) => m.setSearch(e.target.value)}
                        placeholder="Search modifiers or groups..."
                        style={{ width: '100%', height: 34, paddingLeft: 32, paddingRight: m.search ? 32 : 12, border: `1px solid ${T.cardBorder}`, borderRadius: 8, fontSize: 12, fontFamily: T.font, color: T.t1, background: T.surface, outline: 'none', boxSizing: 'border-box' }}
                    />
                    {m.search && (
                        <button type="button" onClick={() => m.setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: T.t3, display: 'flex', alignItems: 'center' }}>
                            <span className="material-symbols-rounded" style={{ fontSize: 16 }}>close</span>
                        </button>
                    )}
                </div>
            </div>

            <div style={{ fontSize: 11, color: T.t3, marginBottom: 12 }}>
                Showing {m.rangeFrom}–{m.rangeTo} of {m.totalCount} {m.view === 'grouped' ? 'modifier groups' : 'modifiers'}
            </div>

            {m.loading ? (
                <div className="rpt-flex-col rpt-gap-10">
                    {[1, 2, 3, 4, 5].map((i) => <Bone key={i} h={16} r={4} />)}
                </div>
            ) : m.failure ? (
                <SectionError kpiName="modifier performance" onRetry={m.retry} size="medium" minHeight={220} />
            ) : !m.hasAnyPricedData ? (
                <ModifierEmptyState />
            ) : m.totalCount === 0 ? (
                <ModifierEmptyState minHeight={140} />
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: T.font }}>
                        <thead>
                            <tr>
                                <th style={{ fontSize: 11, fontWeight: 600, color: T.t3, textTransform: 'uppercase', textAlign: 'left', padding: '10px 12px', borderBottom: `1px solid ${T.divider}`, whiteSpace: 'nowrap' }}>Modifier Group</th>
                                <th style={{ fontSize: 11, fontWeight: 600, color: T.t3, textTransform: 'uppercase', textAlign: 'left', padding: '10px 12px', borderBottom: `1px solid ${T.divider}`, whiteSpace: 'nowrap' }}>Modifier</th>
                                {/* onClick lives on the span (sized to its own content), not the <th> —
                                    a <th> always spans the full column width, so putting it there made
                                    clicking anywhere across that width (including empty space) sort. */}
                                <th style={{ fontSize: 11, fontWeight: 600, color: T.t3, textTransform: 'uppercase', textAlign: 'right', padding: '10px 12px', borderBottom: `1px solid ${T.divider}`, whiteSpace: 'nowrap' }}>
                                    <span onClick={m.toggleSort} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, cursor: 'pointer', userSelect: 'none' }}>Attach Rate<KpiTooltip text="Share of the parent item's orders that included this modifier. Higher means guests choose it more often when it's offered. Grouped rows show the share that included any modifier from the group." />{m.sortAsc ? ' ↑' : ' ↓'}</span>
                                </th>
                                <th style={{ fontSize: 11, fontWeight: 600, color: T.t3, textTransform: 'uppercase', textAlign: 'right', padding: '10px 12px', borderBottom: `1px solid ${T.divider}`, whiteSpace: 'nowrap' }}>Qty</th>
                                <th style={{ fontSize: 11, fontWeight: 600, color: T.t3, textTransform: 'uppercase', textAlign: 'right', padding: '10px 12px', borderBottom: `1px solid ${T.divider}`, whiteSpace: 'nowrap' }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, justifyContent: 'flex-end' }}>Upcharge Revenue<KpiTooltip text="Extra revenue from modifier upcharges. Already counted inside Total Item Revenue — shown here for visibility, not added on top." /></span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {m.view === 'individual' ? m.individualRows.map((r) => (
                                <tr key={r.id} style={{ borderBottom: `1px solid ${T.divider}` }} onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                                    <td style={{ fontSize: 12, color: T.t2, padding: '10px 12px', whiteSpace: 'nowrap' }}>{r.groupName}</td>
                                    <td style={{ fontSize: 12, fontWeight: 500, color: T.t1, padding: '10px 12px', whiteSpace: 'nowrap' }}>{r.modifierName}</td>
                                    <td style={{ fontSize: 12, color: T.t1, textAlign: 'right', padding: '10px 12px' }}>{fmtAttachRate(r.attachRate)}</td>
                                    <td style={{ fontSize: 12, color: T.t1, textAlign: 'right', padding: '10px 12px' }}>{r.qty}</td>
                                    <td style={{ fontSize: 12, fontWeight: 600, color: T.t1, textAlign: 'right', padding: '10px 12px' }}>{fmtFull(r.revenue)}</td>
                                </tr>
                            )) : m.groupedRows.map((g) => {
                                const expanded = m.expandedGroups.has(g.id);
                                return (
                                    <Fragment key={g.id}>
                                        <tr onClick={() => m.toggleGroup(g.id)} style={{ borderBottom: `1px solid ${T.divider}`, cursor: 'pointer' }} onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                                            <td style={{ fontSize: 12, fontWeight: 600, color: T.t1, padding: '10px 12px', whiteSpace: 'nowrap' }}>
                                                <span className="material-symbols-rounded" style={{ fontSize: 15, color: T.t3, verticalAlign: 'middle', marginRight: 4, display: 'inline-block', transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>chevron_right</span>
                                                {g.groupName}
                                            </td>
                                            <td style={{ fontSize: 12, color: T.t3, padding: '10px 12px', whiteSpace: 'nowrap' }}>{g.modifiers.length} modifier{g.modifiers.length === 1 ? '' : 's'}</td>
                                            <td style={{ fontSize: 12, color: T.t1, textAlign: 'right', padding: '10px 12px' }}>{fmtAttachRate(g.attachRate)}</td>
                                            <td style={{ fontSize: 12, color: T.t1, textAlign: 'right', padding: '10px 12px' }}>{g.qty}</td>
                                            <td style={{ fontSize: 12, fontWeight: 600, color: T.t1, textAlign: 'right', padding: '10px 12px' }}>{fmtFull(g.revenue)}</td>
                                        </tr>
                                        {expanded && g.modifiers.map((r) => (
                                            <tr key={r.id} style={{ borderBottom: `1px solid ${T.divider}`, background: T.hover }}>
                                                <td style={{ padding: '8px 12px' }} />
                                                <td style={{ fontSize: 12, color: T.t2, padding: '8px 12px 8px 28px', whiteSpace: 'nowrap' }}>{r.modifierName}</td>
                                                <td style={{ fontSize: 12, color: T.t2, textAlign: 'right', padding: '8px 12px' }}>{fmtAttachRate(r.attachRate)}</td>
                                                <td style={{ fontSize: 12, color: T.t2, textAlign: 'right', padding: '8px 12px' }}>{r.qty}</td>
                                                <td style={{ fontSize: 12, fontWeight: 500, color: T.t2, textAlign: 'right', padding: '8px 12px' }}>{fmtFull(r.revenue)}</td>
                                            </tr>
                                        ))}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {!m.loading && !m.failure && m.hasAnyPricedData && m.totalCount > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}`, gap: 8, flexWrap: 'wrap' }}>
                    <ModifierPageSizeSelect value={m.pageSize} options={MODIFIER_PAGE_SIZE_OPTIONS} onChange={m.setPageSize} />
                    {m.pageSize !== 'All' && m.totalPages > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <button onClick={() => m.setPage((p: number) => Math.max(1, p - 1))} disabled={m.page <= 1} style={{ height: 26, padding: '0 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: m.page <= 1 ? T.hover : T.card, color: m.page <= 1 ? T.t3 : T.t1, cursor: m.page <= 1 ? 'default' : 'pointer', fontSize: 12 }}>‹ Prev</button>
                            <span style={{ fontSize: 11, color: T.t3 }}>Page {m.page} of {m.totalPages}</span>
                            <button onClick={() => m.setPage((p: number) => Math.min(m.totalPages, p + 1))} disabled={m.page >= m.totalPages} style={{ height: 26, padding: '0 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: m.page >= m.totalPages ? T.hover : T.card, color: m.page >= m.totalPages ? T.t3 : T.t1, cursor: m.page >= m.totalPages ? 'default' : 'pointer', fontSize: 12 }}>Next ›</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Mobile: Modifier Performance card list ─────────────────────────────────
function MobileModifierPerformanceCard({ branch, locationId, token, startDate, endDate, exportDateRangeLabel }: any) {
    const m = useModifierPerformanceTable(locationId, token, startDate, endDate);

    return (
        <div className="rpt-card" style={{ padding: 14 }}>
            <div className="rpt-flex-row rpt-flex-between rpt-flex-center" style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>Modifier Performance</div>
                {!m.loading && !m.failure && m.hasAnyPricedData && m.totalCount > 0 && (
                    <ReportSectionExportButton
                        dateRangeLabel={exportDateRangeLabel}
                        variant="mobile"
                        title="Modifier Performance"
                        filename={`product-modifiers-${startDate || 'period'}${endDate && endDate !== startDate ? `-to-${endDate}` : ''}`}
                        buildExport={async (format, opts) => {
                            const rows = await m.fetchAllForExport(opts?.locationId);
                            return {
                                headers: ['Modifier Group', 'Modifier', 'Attach Rate', 'Qty', 'Upcharge Revenue'],
                                rows: rows.map((r) => [
                                    r.groupName,
                                    r.modifierName,
                                    fmtAttachRate(r.attachRate),
                                    r.qty,
                                    formatMoneyForExport(r.revenue),
                                ]),
                            };
                        }}
                    />
                )}
            </div>

            <div className="rpt-flex-row rpt-flex-center rpt-gap-6" style={{ marginBottom: 8 }}>
                <ModifierViewToggle view={m.view} onChange={m.setView} compact />
                <ModifierSingleSelect value={m.groupFilter} allLabel="All groups" options={m.groupOptions} onChange={m.setGroupFilter} compact />
            </div>
            <div className="rpt-flex-row rpt-flex-center rpt-gap-6" style={{ marginBottom: 8 }}>
                <ModifierSingleSelect value={m.itemFilter} allLabel="All items" options={m.itemOptions} onChange={m.setItemFilter} compact />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: T.t2, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={m.showUnpriced} onChange={(e) => m.setShowUnpriced(e.target.checked)} style={{ width: 13, height: 13, accentColor: T.accent, cursor: 'pointer' }} />
                    Show unpriced
                </label>
            </div>
            <div style={{ position: 'relative', marginBottom: 8 }}>
                <span className="material-symbols-rounded" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: T.t3, pointerEvents: 'none' }}>search</span>
                <input
                    type="text"
                    value={m.search}
                    onChange={(e) => m.setSearch(e.target.value)}
                    placeholder="Search modifiers or groups..."
                    style={{ width: '100%', paddingLeft: 30, paddingRight: m.search ? 30 : 10, paddingTop: 7, paddingBottom: 7, border: `1px solid ${T.cardBorder}`, borderRadius: 8, fontSize: 11, fontFamily: T.font, color: T.t1, background: T.surface, outline: 'none', boxSizing: 'border-box' }}
                />
                {m.search && (
                    <button type="button" onClick={() => m.setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: T.t3, display: 'flex', alignItems: 'center' }}>
                        <span className="material-symbols-rounded" style={{ fontSize: 14 }}>close</span>
                    </button>
                )}
            </div>

            <div style={{ fontSize: 9, color: T.t3, marginBottom: 4 }}>Showing {m.rangeFrom}–{m.rangeTo} of {m.totalCount} {m.view === 'grouped' ? 'modifier groups' : 'modifiers'}</div>
            <div className="rpt-flex-row rpt-gap-12" style={{ marginBottom: 6 }}>
                {/* KpiTooltip stops propagation on its own tap (isMobile mode), so
                    without a bounded hit-box it could swallow taps meant for the
                    adjacent arrow — sorting silently did nothing. Fixed widths on
                    the icon and the arrow (not a reorder) give each its own
                    non-overlapping tap target while keeping the original
                    label → icon → arrow layout. */}
                <span onClick={m.toggleSort} style={{ fontSize: 9, color: T.t3, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    Attach Rate
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 14, height: 14, flexShrink: 0 }}>
                        <KpiTooltip text="Share of the parent item's orders that included this modifier. Grouped rows show the share that included any modifier from the group." isMobile />
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 12, flexShrink: 0 }}>{m.sortAsc ? '↑' : '↓'}</span>
                </span>
                <span style={{ fontSize: 9, color: T.t3, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    Upcharge Revenue<KpiTooltip text="Extra revenue from modifier upcharges. Already counted inside Total Item Revenue — shown here for visibility, not added on top." isMobile />
                </span>
            </div>

            {m.loading ? (
                <div className="rpt-flex-col rpt-gap-8">
                    {[1, 2, 3, 4].map((i) => <Bone key={i} h={14} r={4} />)}
                </div>
            ) : m.failure ? (
                <SectionError kpiName="modifier performance" onRetry={m.retry} minHeight={140} />
            ) : !m.hasAnyPricedData ? (
                <ModifierEmptyState minHeight={140} />
            ) : m.totalCount === 0 ? (
                <ModifierEmptyState minHeight={120} />
            ) : m.view === 'individual' ? (
                m.individualRows.map((r) => (
                    <div key={r.id} style={{ padding: '9px 0', borderBottom: `1px solid ${T.divider}` }}>
                        <div className="rpt-flex-row rpt-flex-between">
                            <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, maxWidth: '22ch', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.modifierName} <span style={{ fontWeight: 400, color: T.t3 }}>· {r.groupName}</span></div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: T.t1 }}>{fmtFull(r.revenue)}</div>
                        </div>
                        <div style={{ fontSize: 9, color: T.t3, marginTop: 2 }}>Attach {fmtAttachRate(r.attachRate)} · Qty {r.qty}</div>
                    </div>
                ))
            ) : (
                m.groupedRows.map((g) => {
                    const expanded = m.expandedGroups.has(g.id);
                    return (
                        <div key={g.id}>
                            <div onClick={() => m.toggleGroup(g.id)} style={{ padding: '9px 0', borderBottom: expanded ? 'none' : `1px solid ${T.divider}`, cursor: 'pointer' }}>
                                <div className="rpt-flex-row rpt-flex-between">
                                    <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, display: 'flex', alignItems: 'center' }}>
                                        <span className="material-symbols-rounded" style={{ fontSize: 14, color: T.t3, marginRight: 3, transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>chevron_right</span>
                                        {g.groupName}
                                    </div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: T.t1 }}>{fmtFull(g.revenue)}</div>
                                </div>
                                <div style={{ fontSize: 9, color: T.t3, marginTop: 2, marginLeft: 17 }}>Attach {fmtAttachRate(g.attachRate)} · Qty {g.qty} · {g.modifiers.length} modifier{g.modifiers.length === 1 ? '' : 's'}</div>
                            </div>
                            {expanded && (
                                <div style={{ background: T.hover, borderRadius: 8, padding: '4px 10px', marginBottom: 4 }}>
                                    {g.modifiers.map((r) => (
                                        <div key={r.id} style={{ padding: '7px 0', borderBottom: `1px solid ${T.divider}` }}>
                                            <div className="rpt-flex-row rpt-flex-between">
                                                <div style={{ fontSize: 11, color: T.t2 }}>{r.modifierName}</div>
                                                <div style={{ fontSize: 12, fontWeight: 600, color: T.t2 }}>{fmtFull(r.revenue)}</div>
                                            </div>
                                            <div style={{ fontSize: 9, color: T.t3, marginTop: 1 }}>Attach {fmtAttachRate(r.attachRate)} · Qty {r.qty}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })
            )}

            {!m.loading && !m.failure && m.hasAnyPricedData && m.totalCount > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, paddingTop: 8, borderTop: `1px solid ${T.divider}`, gap: 6, flexWrap: 'wrap' }}>
                    <ModifierPageSizeSelect value={m.pageSize} options={MODIFIER_PAGE_SIZE_OPTIONS} onChange={m.setPageSize} compact />
                    {m.pageSize !== 'All' && m.totalPages > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <button onClick={() => m.setPage((p: number) => Math.max(1, p - 1))} disabled={m.page <= 1} style={{ height: 22, padding: '0 8px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: m.page <= 1 ? T.hover : T.card, color: m.page <= 1 ? T.t3 : T.t1, cursor: m.page <= 1 ? 'default' : 'pointer', fontSize: 10 }}>‹</button>
                            <span style={{ fontSize: 9, color: T.t3 }}>{m.page}/{m.totalPages}</span>
                            <button onClick={() => m.setPage((p: number) => Math.min(m.totalPages, p + 1))} disabled={m.page >= m.totalPages} style={{ height: 22, padding: '0 8px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: m.page >= m.totalPages ? T.hover : T.card, color: m.page >= m.totalPages ? T.t3 : T.t1, cursor: m.page >= m.totalPages ? 'default' : 'pointer', fontSize: 10 }}>›</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Inner content (must render inside DesktopLayout so useDesktopTopBar works) ─
function ProductMenuContent() {
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    const setTopBarProps = useDesktopTopBar();
    const [datePeriod, setDatePeriod] = useSharedReportDateFilter('Yesterday', { excludeToday: true });
    const [sellerView, setSellerView] = useState('top');

    const locationId = branch?.id ?? branch?.locationId ?? '';
    const token = auth?.accessToken ?? auth?.access_token ?? null;

    const storeTz = useStoreTimeZone();
    const today = todayStr(storeTz);
    const yesterday = yesterdayStr(storeTz);
    const dateRange = useMemo(() => getStartEndFromFilter(datePeriod, today, yesterday, storeTz), [datePeriod, today, yesterday, storeTz]);
    const exportDateRangeLabel = exportPeriodFor(datePeriod, dateRange)?.label ?? datePeriod;
    const startDate = dateRange?.startDate ?? '';
    const endDate = dateRange?.endDate ?? '';

    useEffect(() => {
        setTopBarProps({
            breadcrumbs: 'Reports',
            title: 'Product & Menu Performance',
            dateFilters: {
                value: datePeriod,
                onChange: setDatePeriod,
                options: DATE_PRESETS_DESKTOP_NO_TODAY,
            },
            onExport: () => { },
        });
    }, [setTopBarProps, datePeriod]);

    const [selectedItem, setSelectedItem] = useState<any>(null);
    const [trendItems, setTrendItems] = useState([]);
    const [tableCategoryFilter, setTableCategoryFilter] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState<{ categoryName: string; categoryId: string }[]>([]);
    const itemTableHydratedRef = useRef(false);
    const [itemTablePage, setItemTablePage] = useState(1);
    const [itemSearch, setItemSearch] = useState('');
    const [itemSearchRows, setItemSearchRows] = useState<any[]>([]);
    const [itemSearchTotal, setItemSearchTotal] = useState(0);
    const [itemSearchPagesTotal, setItemSearchPagesTotal] = useState(1);
    const [itemSearchPage, setItemSearchPage] = useState(1);
    const [itemSearchLoading, setItemSearchLoading] = useState(false);
    // Client-side sort for Item / Qty Sold / Revenue — see FE_SORT_FIELDS above.
    // Category / PMIX % are not sortable at all.
    const [feSortField, setFeSortField] = useState<string | null>(null);
    const [feSortAsc, setFeSortAsc] = useState(true);
    const [feSortRows, setFeSortRows] = useState<any[] | null>(null);
    const [feSortLoading, setFeSortLoading] = useState(false);
    const [feSortPage, setFeSortPage] = useState(1);
    const handleSortChange = useCallback((field: string) => {
        if (!FE_SORT_FIELDS.has(field)) return;
        if (feSortField === field) setFeSortAsc((a) => !a);
        else { setFeSortField(field); setFeSortAsc(true); }
        setFeSortPage(1);
    }, [feSortField]);

    // Product report data from APIs
    const [topItemsRevenue, setTopItemsRevenue] = useState([]);
    const [bottomItemsRevenue, setBottomItemsRevenue] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const [pmixData, setPmixData] = useState([]);
    // Sales by Category (ZAR-1223) — replaces the PMIX Top-15 card.
    const catSales = useCategorySalesData(locationId, token, startDate, endDate);
    const [channelItems, setChannelItems] = useState([]);
    const [channelSeries, setChannelSeries] = useState([]);
    const [channelCurrentPage, setChannelCurrentPage] = useState(1);
    const [channelTotalPages, setChannelTotalPages] = useState(1);
    const [channelTotalElements, setChannelTotalElements] = useState(0);
    const [channelPageSize, setChannelPageSize] = useState(10);
    const [channelLoading, setChannelLoading] = useState(false);
    const [channelError, setChannelError] = useState(false);
    const [channelRetryTick, setChannelRetryTick] = useState(0);
    const [addonData, setAddonData] = useState([]);
    const [refundData, setRefundData] = useState([]);
    const [fullTableData, setFullTableData] = useState([]);
    const [itemTableTotal, setItemTableTotal] = useState(0);
    const [itemTablePagesTotal, setItemTablePagesTotal] = useState(1);
    const [itemTableLoading, setItemTableLoading] = useState(false);
    const [categoryItemOverall, setCategoryItemOverall] = useState({ gross: null, qty: null, avgPerItem: null });
    const [productLoading, setProductLoading] = useState(false);
    const [productError, setProductError] = useState<any>(null);
    const [productRetryTick, setProductRetryTick] = useState(0);
    const channelRangeStart = channelTotalElements === 0 ? 0 : (channelCurrentPage - 1) * channelPageSize + 1;
    const channelRangeEnd = channelTotalElements === 0 ? 0 : Math.min(channelCurrentPage * channelPageSize, channelTotalElements);

    const retryProduct = () => {
        if (locationId && startDate && endDate) {
            productDatasetCache.delete(getProductCacheKey(locationId, startDate, endDate, itemTablePage - 1));
        }
        setProductRetryTick((n) => n + 1);
    };
    const retryChannel = () => {
        if (locationId && startDate && endDate) {
            channelDatasetCache.delete(getChannelCacheKey(locationId, startDate, endDate, channelCurrentPage, channelPageSize));
        }
        setChannelRetryTick((n) => n + 1);
    };

    const allItemNames = useMemo(() => {
        const names = new Set();
        topItemsRevenue.forEach((i) => names.add(i.name));
        bottomItemsRevenue.forEach((i) => names.add(i.name));
        fullTableData.forEach((i) => names.add(i.name));
        return Array.from(names);
    }, [topItemsRevenue, bottomItemsRevenue, fullTableData]);

    const topItemsRevenueDisplay = useMemo(() => topItemsRevenue.slice(0, 10), [topItemsRevenue]);
    const bottomItemsRevenueDisplay = useMemo(() => bottomItemsRevenue.slice(0, 5), [bottomItemsRevenue]);

    // Category dropdown options come straight from the API (categoryName + categoryId).
    const allCategories = useMemo(
        () => categoryOptions.map((c) => c.categoryName).filter(Boolean).sort(),
        [categoryOptions]
    );
    // Selected category names → comma-joined categoryId string for the item-table API.
    const selectedCategoryIds = useMemo(() => {
        if (tableCategoryFilter.length === 0) return '';
        const nameToId = new Map(categoryOptions.map((c) => [c.categoryName, c.categoryId]));
        return tableCategoryFilter.map((name) => nameToId.get(name)).filter(Boolean).join(',');
    }, [tableCategoryFilter, categoryOptions]);

    // Fetch the category dropdown list for the current location + date range.
    useEffect(() => {
        if (!locationId || !token || !startDate || !endDate) return;
        let cancelled = false;
        getCategoryDropdown({ locationId, startDate, endDate, token })
            .then((res) => {
                if (cancelled) return;
                const list = normList(res)
                    .map((c) => ({
                        categoryName: c?.categoryName ?? c?.name ?? '',
                        categoryId: c?.categoryId ?? c?.id ?? '',
                    }))
                    .filter((c) => c.categoryName && c.categoryId);
                setCategoryOptions(list);
            })
            .catch(() => { if (!cancelled) setCategoryOptions([]); });
        return () => { cancelled = true; };
    }, [locationId, startDate, endDate, token]);

    // Drop any selected categories that no longer exist for the new date range / location.
    useEffect(() => {
        setTableCategoryFilter((prev) => {
            if (prev.length === 0) return prev;
            const valid = prev.filter((name) => allCategories.includes(name));
            return valid.length === prev.length ? prev : valid;
        });
    }, [allCategories]);

    useEffect(() => {
        if (allItemNames.length === 0) return;
        setTrendItems((prev) => prev.filter((name) => allItemNames.includes(name)));
    }, [allItemNames]);

    const trendLineColors = [T.accent, T.orange, T.green, '#7C5CFC', '#0EA5E9'];
    const trendData = useMemo(() => {
        if (trendItems.length === 0) return [];
        const days = [];
        for (let i = 29; i >= 0; i--) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            const row = { date: `${d.getMonth() + 1}/${d.getDate()}` };
            trendItems.forEach((name) => { row[name] = 100 + Math.floor(Math.random() * 200); });
            days.push(row);
        }
        return days;
    }, [trendItems]);

    useEffect(() => {
        if (!locationId || !token || !startDate || !endDate) return;
        const opts = { locationId, startDate, endDate, token, filter: 'Overall' };
        const cacheKey = getProductCacheKey(locationId, startDate, endDate, 0);
        const cached = cacheGet(productDatasetCache,cacheKey);
        if (cached) {
            setProductError(null);
            setTopItemsRevenue(cached.topItemsRevenue);
            setBottomItemsRevenue(cached.bottomItemsRevenue);
            setCategoryData(cached.categoryData);
            setCategoryItemOverall(cached.categoryItemOverall);
            setPmixData(cached.pmixData);
            setAddonData([]);
            setRefundData(cached.refundData);
            setFullTableData(cached.fullTableData);
            setItemTableTotal(cached.itemTableTotal ?? 0);
            setItemTablePagesTotal(cached.itemTablePages ?? 1);
            return;
        }
        setProductLoading(true);
        setProductError(null);
        fetchProductMenuDataset(opts, 0, ITEM_TABLE_PAGE_SIZE_CONST)
            .then((dataset) => {
                cacheSet(productDatasetCache,cacheKey, dataset);
                setTopItemsRevenue(dataset.topItemsRevenue);
                setBottomItemsRevenue(dataset.bottomItemsRevenue);
                setCategoryData(dataset.categoryData);
                setCategoryItemOverall(dataset.categoryItemOverall);
                setPmixData(dataset.pmixData);
                setAddonData([]);
                setRefundData(dataset.refundData);
                setFullTableData(dataset.fullTableData);
                setItemTableTotal(dataset.itemTableTotal ?? 0);
                setItemTablePagesTotal(dataset.itemTablePages ?? 1);
            })
            .catch((err) => {
                setProductError(err?.message ?? 'Failed to load product data');
                setTopItemsRevenue([]);
                setBottomItemsRevenue([]);
                setCategoryData([]);
                setPmixData([]);
                setAddonData([]);
                setRefundData([]);
                setFullTableData([]);
                setItemTableTotal(0);
                setItemTablePagesTotal(1);
                setCategoryItemOverall({ gross: null, qty: null, avgPerItem: null });
            })
            .finally(() => setProductLoading(false));
    }, [locationId, startDate, endDate, token, productRetryTick]);

    // Separate effect: refetch the item table when the page or the category filter changes.
    // The category filter is applied server-side via the categoryId query param.
    useEffect(() => {
        if (feSortField) return; // Item/Qty Sold/Revenue sort is handled client-side (see feSort effect below)
        if (!locationId || !token || !startDate || !endDate) return;
        if (itemSearch.trim() !== '') return; // search-mode has its own paging
        if (itemTablePage === 1 && !selectedCategoryIds) {
            // Page 1, no filter: restore from the main dataset cache (covers 2→1 navigation)
            const cached = cacheGet(productDatasetCache,getProductCacheKey(locationId, startDate, endDate, 0));
            if (cached) {
                setFullTableData(cached.fullTableData);
                setItemTableTotal(cached.itemTableTotal ?? 0);
                setItemTablePagesTotal(cached.itemTablePages ?? 1);
                itemTableHydratedRef.current = true;
                return;
            }
            // Cache expired (e.g. a filter was cleared after the TTL). On the very first
            // load the main bundle effect owns this fetch, so skip until hydrated.
            if (!itemTableHydratedRef.current) return;
        }
        const pageIndex = itemTablePage - 1;
        setItemTableLoading(true);
        getProductCategoryItemSales({ locationId, startDate, endDate, page: pageIndex, size: ITEM_TABLE_PAGE_SIZE_CONST, categoryId: selectedCategoryIds, token })
            .then((res) => {
                const rows = getCategoryItemSalesRows(res).map(mapCategoryItemSalesRow);
                setFullTableData(rows);
                setItemTableTotal(readTableTotalItems(res, rows));
                setItemTablePagesTotal(readTableTotalPages(res));
                itemTableHydratedRef.current = true;
            })
            .catch(() => { /* keep current page data */ })
            .finally(() => setItemTableLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemTablePage, selectedCategoryIds, feSortField]);

    useEffect(() => {
        setChannelCurrentPage(1);
    }, [locationId, startDate, endDate, token]);

    useEffect(() => {
        if (!locationId || !token || !startDate || !endDate) return;
        const cacheKey = getChannelCacheKey(locationId, startDate, endDate, channelCurrentPage, channelPageSize);
        const cached = cacheGet(channelDatasetCache,cacheKey);
        if (cached) {
            setChannelItems(cached.rows);
            setChannelSeries(cached.series);
            setChannelTotalPages(cached.totalPages);
            setChannelTotalElements(cached.totalElements);
            if (cached.pageSize > 0) setChannelPageSize(cached.pageSize);
            setChannelError(false);
            return;
        }
        setChannelLoading(true);
        setChannelError(false);
        getCategoryChannelSummary({
            locationId,
            startDate,
            endDate,
            page: channelCurrentPage - 1,
            size: channelPageSize,
            groupByCategory: false,
            token,
        })
            .then((res) => {
                const mapped = mapCategoryChannelSummaryRows(res);
                cacheSet(channelDatasetCache,cacheKey, mapped);
                setChannelItems(mapped.rows);
                setChannelSeries(mapped.series);
                setChannelTotalPages(mapped.totalPages);
                setChannelTotalElements(mapped.totalElements);
                if (mapped.pageSize > 0) setChannelPageSize(mapped.pageSize);
                setChannelError(false);
            })
            .catch(() => {
                setChannelItems([]);
                setChannelSeries([]);
                setChannelTotalPages(1);
                setChannelTotalElements(0);
                setChannelError(true);
            })
            .finally(() => setChannelLoading(false));
    }, [locationId, startDate, endDate, token, channelCurrentPage, channelPageSize, channelRetryTick]);

    useEffect(() => {
        if (allItemNames.length > 0 && trendItems.length === 0) setTrendItems(allItemNames.slice(0, 3));
    }, [allItemNames.length]);

    // Category filtering is now applied server-side (via categoryId), so the table
    // shows whatever the API returns for the current page + selected categories.
    const filteredTableData = fullTableData;

    const isItemSearchActive = itemSearch.trim() !== '';
    const ITEM_TABLE_PAGE_SIZE = ITEM_TABLE_PAGE_SIZE_CONST;

    // Both search and non-search use server-side pagination: trust the API's
    // totalItems (record count) and totalPages (page count) instead of recomputing.
    // When a client-side sort (Item/Qty Sold/Revenue) is active, the fully-fetched +
    // locally-sorted set (feSort*) takes over instead.
    const feSortedRows = useMemo(() => {
        if (!feSortField || !feSortRows) return null;
        const key = FE_SORT_ROW_KEY[feSortField];
        return [...feSortRows].sort((a, b) => compareFeSortRows(a, b, key, feSortAsc));
    }, [feSortField, feSortRows, feSortAsc]);
    const feSortActive = !!feSortField && !!feSortedRows;
    const feSortTotalItems = feSortedRows ? feSortedRows.length : 0;
    const feSortTotalPages = feSortedRows ? Math.max(1, Math.ceil(feSortedRows.length / ITEM_TABLE_PAGE_SIZE_CONST)) : 1;
    const feSortPageRows = useMemo(() => {
        if (!feSortedRows) return [];
        const start = (feSortPage - 1) * ITEM_TABLE_PAGE_SIZE_CONST;
        return feSortedRows.slice(start, start + ITEM_TABLE_PAGE_SIZE_CONST);
    }, [feSortedRows, feSortPage]);

    const activeTableData = feSortActive ? feSortPageRows : (isItemSearchActive ? itemSearchRows : filteredTableData);
    const activeTotalItems = feSortActive ? feSortTotalItems : (isItemSearchActive ? itemSearchTotal : itemTableTotal);
    const activeTotalPages = feSortActive ? feSortTotalPages : (isItemSearchActive ? itemSearchPagesTotal : itemTablePagesTotal);
    const activeTablePage = feSortActive ? feSortPage : (isItemSearchActive ? itemSearchPage : itemTablePage);
    const setActiveTablePage = feSortActive ? setFeSortPage : (isItemSearchActive ? setItemSearchPage : setItemTablePage);
    const showItemTablePagination = activeTotalPages > 1;
    // Item table is fetching (category/page change, search, or client-sort fetch-all) — shimmer rows + pagination.
    const tableLoading = feSortLoading || itemSearchLoading || itemTableLoading;

    // Export must include the WHOLE dataset, not just the current page. Refetch
    // /product/insights/category-item-sales once with size = total items (the
    // initial API's totalItems), carrying the active category filter / search so
    // the export matches the on-screen scope (all data when nothing is filtered).
    // The refetch drops whatever order the backend returns, so if an Item/Qty
    // Sold/Revenue client-sort is active, re-apply it here too — otherwise the
    // export would land in a different order than what's on screen.
    const fetchAllItemTableRows = async (overrideLocationId?: string) => {
        const loc = overrideLocationId ?? locationId;
        // A different outlet's row count isn't known ahead of time — probe first,
        // then fetch the whole set. The current outlet keeps using its already-known count.
        let size = activeTotalItems || activeTableData.length || 1;
        if (overrideLocationId) {
            const probe = await getProductCategoryItemSales({
                locationId: loc, startDate, endDate, token,
                page: 0, size: 1,
                categoryId: selectedCategoryIds || undefined,
                search: isItemSearchActive ? itemSearch.trim() : undefined,
            });
            size = (probe as any)?.totalElements ?? (probe as any)?.total ?? size;
        }
        const res = await getProductCategoryItemSales({
            locationId: loc, startDate, endDate, token,
            page: 0, size,
            categoryId: selectedCategoryIds || undefined,
            search: isItemSearchActive ? itemSearch.trim() : undefined,
        });
        const rows = getCategoryItemSalesRows(res).map(mapCategoryItemSalesRow);
        if (!feSortField) return rows;
        const key = FE_SORT_ROW_KEY[feSortField];
        return [...rows].sort((a, b) => compareFeSortRows(a, b, key, feSortAsc));
    };

    // Item/Qty Sold/Revenue sort clicked: fetch the whole current scope once (size =
    // current totalItems) — feSortedRows above then sorts it in memory. Re-runs only
    // when the field/scope changes, not on
    // every direction toggle (that just re-sorts the already-fetched rows).
    useEffect(() => {
        if (!feSortField) { setFeSortRows(null); setFeSortLoading(false); return; }
        if (!locationId || !token || !startDate || !endDate) return;
        let cancelled = false;
        setFeSortLoading(true);
        fetchAllItemTableRows()
            .then((rows) => { if (!cancelled) setFeSortRows(rows); })
            .catch(() => { if (!cancelled) setFeSortRows([]); })
            .finally(() => { if (!cancelled) setFeSortLoading(false); });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feSortField, locationId, startDate, endDate, token, selectedCategoryIds, isItemSearchActive]);

    const paginatedTableData = useMemo(() => {
        if (feSortActive) return feSortPageRows;
        if (isItemSearchActive) return itemSearchRows;
        return filteredTableData;
    }, [feSortActive, feSortPageRows, isItemSearchActive, itemSearchRows, filteredTableData]);

    // Reset to the first page (initial pagination state) whenever the category filter
    // or date range changes, so the next item-table fetch starts at page 0.
    useEffect(() => { setItemTablePage(1); }, [tableCategoryFilter, startDate, endDate]);

    // Any change to the active filter/search/date scope invalidates the previously
    // fetched "sort all items" snapshot — drop back to server-driven sort/paging.
    useEffect(() => {
        setFeSortField(null);
        setFeSortRows(null);
        setFeSortPage(1);
    }, [tableCategoryFilter, startDate, endDate, itemSearch, locationId]);

    // Debounced item search — resets to page 1 on new text
    useEffect(() => {
        const trimmed = itemSearch.trim();
        if (!trimmed) { setItemSearchRows([]); setItemSearchTotal(0); setItemSearchPagesTotal(1); setItemSearchPage(1); return; }
        if (!locationId || !token) return;
        setItemSearchPage(1);
        setItemSearchLoading(true);
        const timer = setTimeout(() => {
            getProductCategoryItemSales({ locationId, startDate, endDate, page: 0, size: ITEM_TABLE_PAGE_SIZE, search: trimmed, token })
                .then((res) => {
                    const rows = getCategoryItemSalesRows(res).map(mapCategoryItemSalesRow);
                    setItemSearchRows(rows);
                    setItemSearchTotal(readTableTotalItems(res, rows));
                    setItemSearchPagesTotal(readTableTotalPages(res));
                })
                .catch(() => { setItemSearchRows([]); setItemSearchTotal(0); setItemSearchPagesTotal(1); })
                .finally(() => setItemSearchLoading(false));
        }, 400);
        return () => clearTimeout(timer);
    }, [itemSearch, locationId, startDate, endDate, token]);

    // Fetch new page when itemSearchPage changes (only when search is active)
    useEffect(() => {
        if (!isItemSearchActive || itemSearchPage === 1) return;
        if (!locationId || !token) return;
        setItemSearchLoading(true);
        getProductCategoryItemSales({ locationId, startDate, endDate, page: itemSearchPage - 1, size: ITEM_TABLE_PAGE_SIZE, search: itemSearch.trim(), token })
            .then((res) => {
                const rows = getCategoryItemSalesRows(res).map(mapCategoryItemSalesRow);
                setItemSearchRows(rows);
                setItemSearchTotal(readTableTotalItems(res, rows));
                setItemSearchPagesTotal(readTableTotalPages(res));
            })
            .catch(() => setItemSearchRows([]))
            .finally(() => setItemSearchLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemSearchPage]);

    const kpiTotalRevenue = useMemo(
        () => (categoryItemOverall.gross != null ? categoryItemOverall.gross : fullTableData.reduce((s, r) => s + r.revenue, 0)),
        [categoryItemOverall.gross, fullTableData]
    );
    const kpiTotalQty = useMemo(
        () => (categoryItemOverall.qty != null ? categoryItemOverall.qty : fullTableData.reduce((s, r) => s + r.qty, 0)),
        [categoryItemOverall.qty, fullTableData]
    );
    const kpiAvgPerItem = useMemo(
        () => (categoryItemOverall.avgPerItem != null ? categoryItemOverall.avgPerItem : (kpiTotalQty > 0 ? kpiTotalRevenue / kpiTotalQty : 0)),
        [categoryItemOverall.avgPerItem, kpiTotalRevenue, kpiTotalQty]
    );

    if (productLoading) return <ProductDesktopShimmer />;

    return (
        <>
            <main className="rpt-page" style={{ color: T.t1 }}>
                {productError && (
                    <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 12, padding: '12px 16px', color: T.red, fontSize: 12 }}>
                        {productError}
                    </div>
                )}
                <div className="rpt-flex-row rpt-gap-16">
                    <KPICard label="Total Item Revenue" value={kpiTotalRevenue} tooltip="Item value excluding taxes" />
                    <KPICard label="Total Items Sold" value={kpiTotalQty} isCurrency={false} />
                    <KPICard label="Avg Revenue / Item" value={Math.round(kpiAvgPerItem * 100) / 100} />
                </div>

                <div className="rpt-flex-row rpt-gap-16" style={{ alignItems: 'flex-start' }}>
                    <div style={{ ...cardStyle, flex: 1.2 }}>
                        <div className="rpt-flex-row rpt-flex-between rpt-flex-center" style={{ marginBottom: 16 }}>
                            <p className="rpt-section-title" style={cardTitleStyle}>{sellerView === 'top' ? 'Top 10 Items by Revenue' : 'Bottom 5 Items by Revenue'}</p>
                            <div className="rpt-flex-row rpt-flex-center rpt-gap-6">
                                <FilterPill label="Top 10" active={sellerView === 'top'} onClick={() => setSellerView('top')} />
                                <FilterPill label="Bottom 5" active={sellerView === 'bottom'} onClick={() => setSellerView('bottom')} />
                            </div>
                        </div>
                        {(() => {
                            const sellerData = sellerView === 'top' ? topItemsRevenueDisplay : bottomItemsRevenueDisplay;
                            const hasData = Array.isArray(sellerData) && sellerData.some((r: any) => Number(r?.revenue ?? 0) > 0);
                            const kpiName = sellerView === 'top' ? 'top 10 items by revenue' : 'bottom 5 items by revenue';
                            return hasData
                                ? <HorizontalBarChart data={sellerData} valueKey="revenue" color={sellerView === 'top' ? T.accent : T.red} />
                                : <SectionEmpty kpiName={kpiName} minHeight={240} />;
                        })()}
                    </div>
                    <div style={{ ...cardStyle, flex: 0.8, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                            <p className="rpt-section-title" style={{ ...cardTitleStyle, marginBottom: 0 }}>Top Revenue Streams</p>
                            {categoryData.length > 0 && (
                                <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                    variant="desktop"
                                    title="Top Revenue Streams"
                                    filename="top-revenue-streams"
                                    buildExport={async (format, opts) => {
                                        const cc = branch?.country ?? branch?.countryCode ?? branch?.countryCd;
                                        const money = (n: any) => formatMoneyForExport(n);
                                        // Outlet-scoped export: when an override locationId targets a DIFFERENT
                                        // outlet, refetch revenue streams for that outlet instead of reusing the
                                        // current outlet's already-loaded categoryData (no pagination on this API).
                                        let data = categoryData;
                                        if (opts?.locationId && opts.locationId !== locationId) {
                                            const r = await getSalesByRevenueClass({ locationId: opts.locationId, startDate, endDate, token, filter: 'Overall' });
                                            const list = normList(r);
                                            const rowTotal = list.reduce((s, row) => s + (pickNumDeep(row, ['totalSales', 'total_sales', 'value', 'revenue', 'amount']) ?? 0), 0);
                                            data = sortRevenueStreams(list.map((row) => mapRevenueStreamRow(row, rowTotal)));
                                        }
                                        const total = data.reduce((s, c) => s + c.value, 0);
                                        const totalUnits = data.reduce((s, c) => s + (c.units ?? 0), 0);
                                        const totalAvgPerItem = totalUnits > 0 ? total / totalUnits : null;
                                        return {
                                            totalRow: true,
                                            headers: ['Stream', 'Revenue', 'Share %', 'Units Sold', 'Avg/Item'],
                                            rows: [
                                                ...data.map((cat: any) => [cat.name, money(cat.value), `${cat.pct.toFixed(2)}%`, cat.units != null ? cat.units : '—', cat.avgPerItem != null ? money(cat.avgPerItem) : '—']),
                                                ['Total', money(total), total > 0 ? '100%' : '—', totalUnits > 0 ? totalUnits : '—', totalAvgPerItem != null ? money(totalAvgPerItem) : '—'],
                                            ],
                                        };
                                    }}
                                />
                            )}
                        </div>
                        {productError ? (
                            <SectionError kpiName="top revenue streams" onRetry={retryProduct} minHeight={240} />
                        ) : categoryData.length === 0 ? (
                            <SectionEmpty kpiName="top revenue streams" minHeight={240} />
                        ) : (
                        <>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie data={categoryData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2} minAngle={3} strokeWidth={0}>
                                        {categoryData.map((_, i) => <Cell key={i} fill={categoryColors[i % categoryColors.length]} />)}
                                    </Pie>
                                    <Tooltip content={<DonutTooltip />} wrapperStyle={{ zIndex: 100 }} contentStyle={{ zIndex: 100, background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, boxShadow: T.shadow }} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none', zIndex: 0 }}>
                                <div style={{ fontSize: 10, color: T.t3, fontWeight: 500 }}>Total</div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: T.t1, marginTop: 2 }}>{fmtFull(categoryData.reduce((s, c) => s + c.value, 0))}</div>
                            </div>
                        </div>
                        <div style={{ marginTop: 16 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(64px, 1fr) 100px 46px 60px 68px', gap: 6, paddingBottom: 8, borderBottom: `1px solid ${T.divider}` }}>
                                <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.4, color: T.t3, textTransform: 'uppercase' }}>Stream</span>
                                <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.4, color: T.t3, textTransform: 'uppercase', textAlign: 'right' }}>Revenue</span>
                                <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.4, color: T.t3, textTransform: 'uppercase', textAlign: 'right' }}>Share</span>
                                <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.4, color: T.t3, textTransform: 'uppercase', textAlign: 'right' }}>Units</span>
                                <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.4, color: T.t3, textTransform: 'uppercase', textAlign: 'right' }}>Avg/Item</span>
                            </div>
                            {categoryData.map((cat, i) => (
                                <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(64px, 1fr) 100px 46px 60px 68px', gap: 6, alignItems: 'center', padding: '10px 0' }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                        <span style={{ width: 9, height: 9, borderRadius: 2, background: categoryColors[i % categoryColors.length], display: 'inline-block', flexShrink: 0 }} />
                                        <span style={{ color: T.t1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.name}</span>
                                    </span>
                                    <span style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: 12, color: T.t1, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fmtFull(cat.value)}</span>
                                    <span style={{ fontSize: 11, color: T.t2, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.pct.toFixed(1)}%</span>
                                    <span style={{ fontSize: 11, color: T.t3, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.units != null ? Math.round(cat.units).toLocaleString() : '—'}</span>
                                    <span style={{ fontSize: 11, color: T.t3, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.avgPerItem != null ? fmtFull(cat.avgPerItem) : '—'}</span>
                                </div>
                            ))}
                        </div>
                        </>
                        )}
                    </div>
                </div>

                <div style={cardStyle}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 16 }}>
                        <div>
                            <p className="rpt-section-title" style={{ ...cardTitleStyle, marginBottom: 0 }}>Sales by Category</p>
                            <p style={{ ...cardSubtitleStyle, fontSize: 12, color: T.t3 }}>Top menu categories by revenue</p>
                        </div>
                        {catSales.totalElements > 0 && (
                            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                variant="desktop"
                                title="Sales by Category"
                                filename="sales-by-category"
                                buildExport={async (format, opts) => {
                                    const cats = await catSales.fetchAll(opts?.locationId);
                                    // Every cell carries its own currency symbol now (formatMoneyForExport),
                                    // so this column is text in every format — no more raw-number CSV branch.
                                    const money = (n: number) => formatMoneyForExport(n);
                                    const totalQty = cats.reduce((s, c) => s + (Number(c.quantity) || 0), 0);
                                    const totalValue = cats.reduce((s, c) => s + (Number(c.price) || 0), 0);
                                    return {
                                        totalRow: true,
                                        headers: ['Serial No.', 'Category Name', 'Total Sales Quantity', 'Total Sales Value'],
                                        rows: [
                                            ...cats.map((item, i) => [i + 1, item.categoryName, fmtExportQty(item.quantity), money(item.price)]),
                                            ['', 'Total', fmtExportQty(totalQty), money(totalValue)],
                                        ],
                                    };
                                }}
                            />
                        )}
                    </div>
                    {catSales.loading ? (
                        <div style={{ paddingTop: 4 }}>
                            {[1, 2, 3, 4, 5].map(i => (
                                <div key={i} style={{ marginBottom: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <Bone w={120} h={12} />
                                        <Bone w={56} h={12} />
                                    </div>
                                    <Bone h={6} r={3} />
                                </div>
                            ))}
                        </div>
                    ) : catSales.failure ? (
                        <SectionError kpiName="sales by category" onRetry={catSales.retry} minHeight={200} />
                    ) : catSales.items.length === 0 ? (
                        <SectionEmpty kpiName="sales by category" minHeight={200} />
                    ) : (() => {
                        const maxCatPrice = Math.max(...(catSales.allItems.length ? catSales.allItems : catSales.items).map(item => item.price), 1);
                        return (
                            <>
                                {catSales.items.map((item, i) => (
                                    <div key={i} style={{ marginBottom: i < catSales.items.length - 1 ? 10 : 0 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                            <span style={{ fontSize: 12, fontWeight: 500, color: T.t1 }}>{item.categoryName}</span>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: T.t1, width: 70, textAlign: 'right' }}>{fmtFull(item.price)}</span>
                                        </div>
                                        <div style={{ height: 6, background: T.hover, borderRadius: 3, overflow: 'hidden' }}>
                                            <div style={{ height: 6, borderRadius: 3, width: `${(item.price / maxCatPrice) * 100}%`, background: '#db7172' }} />
                                        </div>
                                    </div>
                                ))}
                                {/* Running total reflects ALL categories, not just this page. */}
                                <div style={{ marginTop: 8, paddingTop: 10, borderTop: `1px solid ${T.divider}`, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.t3 }}>
                                    <span>{catSales.totalElements} categories</span>
                                    <span>Total: <span style={{ color: T.t1, fontWeight: 600 }}>{fmtFull(catSales.grandTotal)}</span></span>
                                </div>
                                {/* Records-per-page + pagination controls */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.divider}`, gap: 8, flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontSize: 11, color: T.t3 }}>Records per page:</span>
                                        <select
                                            value={String(catSales.pageSize)}
                                            onChange={e => catSales.setPageSize(e.target.value === 'All' ? 'All' : Number(e.target.value))}
                                            style={{ height: 26, padding: '0 6px', border: `1px solid ${T.cardBorder}`, borderRadius: 6, fontSize: 12, color: T.t1, background: T.card, cursor: 'pointer' }}
                                        >
                                            {CAT_PAGE_SIZE_OPTIONS.map(n => <option key={String(n)} value={String(n)}>{n}</option>)}
                                        </select>
                                    </div>
                                    {catSales.pageSize !== 'All' && catSales.totalPages > 1 && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <button onClick={() => catSales.setPage(p => Math.max(0, p - 1))} disabled={catSales.page === 0} style={{ height: 26, padding: '0 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: catSales.page === 0 ? T.hover : T.card, color: catSales.page === 0 ? T.t3 : T.t1, cursor: catSales.page === 0 ? 'default' : 'pointer', fontSize: 12 }}>‹ Prev</button>
                                            <span style={{ fontSize: 11, color: T.t3 }}>Page {catSales.page + 1} of {catSales.totalPages}</span>
                                            <button onClick={() => catSales.setPage(p => Math.min(catSales.totalPages - 1, p + 1))} disabled={catSales.page >= catSales.totalPages - 1} style={{ height: 26, padding: '0 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: catSales.page >= catSales.totalPages - 1 ? T.hover : T.card, color: catSales.page >= catSales.totalPages - 1 ? T.t3 : T.t1, cursor: catSales.page >= catSales.totalPages - 1 ? 'default' : 'pointer', fontSize: 12 }}>Next ›</button>
                                        </div>
                                    )}
                                </div>
                            </>
                        );
                    })()}
                </div>

                <div style={cardStyle}>
                    <p className="rpt-section-title" style={cardTitleStyle}>Item Revenue by Channel</p>
                    {channelError ? (
                        <SectionError kpiName="item revenue by channel" onRetry={retryChannel} size="medium" minHeight={260} />
                    ) : channelItems.length === 0 ? (
                        <SectionEmpty kpiName="item revenue by channel" size="medium" minHeight={260} />
                    ) : (
                    <>
                    <ResponsiveContainer width="100%" height={Math.max(280, channelItems.length * 42 + 60)}>
                        <BarChart layout="vertical" data={channelItems} margin={{ left: 10, right: 20, top: 8, bottom: 8 }}>
                            <XAxis
                                type="number"
                                tickFormatter={(v) => fmt(v)}
                                tick={{ fontSize: 11, fill: T.t3 }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                type="category"
                                dataKey="name"
                                width={130}
                                tick={(props) => {
                                    const { x, y, payload } = props;
                                    const s = String(payload?.value ?? '');
                                    const label = s.length > 22 ? s.slice(0, 22) + '…' : s;
                                    return <text x={x} y={y} dy={4} textAnchor="end" fontSize={11} fill={T.t2}>{label}</text>;
                                }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip content={<BarTooltip />} />
                            <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                            {channelSeries.map((series, idx) => (
                                <Bar key={series.key} dataKey={series.key} name={series.label} stackId="a" fill={series.color ?? getChannelColor(series.key, idx)} radius={idx === channelSeries.length - 1 ? [0, 3, 3, 0] : undefined} />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                    <div className="rpt-flex-row rpt-flex-center rpt-gap-10" style={{ justifyContent: 'flex-end', marginTop: 6 }}>
                        <div style={{ fontSize: 12, color: T.t2 }}>
                            {channelRangeStart}-{channelRangeEnd} of {channelTotalElements}
                        </div>
                        <div className="rpt-flex-row rpt-gap-8">
                            <button
                                type="button"
                                onClick={() => setChannelCurrentPage((p) => Math.max(1, p - 1))}
                                disabled={channelCurrentPage <= 1 || channelLoading}
                                style={{
                                    border: `1px solid ${T.cardBorder}`,
                                    borderRadius: 6,
                                    background: channelCurrentPage <= 1 || channelLoading ? T.hover : T.surface,
                                    color: T.t2,
                                    fontSize: 11,
                                    padding: '4px 10px',
                                    cursor: channelCurrentPage <= 1 || channelLoading ? 'not-allowed' : 'pointer',
                                }}
                            >
                                Prev
                            </button>
                            <button
                                type="button"
                                onClick={() => setChannelCurrentPage((p) => Math.min(channelTotalPages, p + 1))}
                                disabled={channelCurrentPage >= channelTotalPages || channelLoading}
                                style={{
                                    border: `1px solid ${T.cardBorder}`,
                                    borderRadius: 6,
                                    background: channelCurrentPage >= channelTotalPages || channelLoading ? T.hover : T.surface,
                                    color: T.t2,
                                    fontSize: 11,
                                    padding: '4px 10px',
                                    cursor: channelCurrentPage >= channelTotalPages || channelLoading ? 'not-allowed' : 'pointer',
                                }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                    </>
                    )}
                </div>

                <div className="rpt-flex-row rpt-gap-16">
                    {/* Add-On Revenue Ranking KPI - commented out
                    <div style={{ ...cardStyle, flex: 1 }}>
                        <p className="rpt-section-title" style={cardTitleStyle}>Add-On Revenue Ranking</p>
                        <div className="rpt-flex-col rpt-gap-10">
                            {addonData.map((item, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{ width: 140, fontSize: 12, fontWeight: 500, color: T.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{item.name}</div>
                                    <div style={{ flex: 1, background: T.hover, borderRadius: 3, height: 6, overflow: 'hidden' }}>
                                        <div style={{ width: `${(item.addon / 3500) * 100}%`, height: '100%', background: T.accent, borderRadius: 3, opacity: 0.6 }} />
                                    </div>
                                    <div style={{ width: 60, fontSize: 11, fontWeight: 600, color: T.t1, textAlign: 'right' }}>{fmtFull(item.addon)}</div>
                                    <div style={{ width: 40, fontSize: 10, fontWeight: 500, color: T.accent, textAlign: 'right', background: T.accentSoft, borderRadius: 4, padding: '2px 6px' }}>{item.rate}%</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    */}
                    {/* Refund & Void Rate by Item — hidden
                    <div style={{ ...cardStyle, flex: 1 }}>
                        <p className="rpt-section-title" style={cardTitleStyle}>Refund & Void Rate by Item</p>
                        <div className="rpt-flex-col rpt-gap-10">
                            {refundData.map((item, i) => (
                                <div key={i} className="rpt-flex-row rpt-flex-center rpt-gap-12">
                                    <div style={{ width: 140, fontSize: 12, fontWeight: 500, color: T.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{item.name}</div>
                                    <div style={{ flex: '0 0 50%', maxWidth: 260, background: T.hover, borderRadius: 3, height: 6, overflow: 'hidden' }}>
                                        <div style={{ width: `${Math.max(2, Number(item.rate) || 0)}%`, height: '100%', background: T.red, borderRadius: 3, opacity: 0.65 }} />
                                    </div>
                                    <div style={{ width: 58, fontSize: 11, fontWeight: 600, color: T.red, textAlign: 'right' }}>{(Number(item.rate) || 0).toFixed(2)}%</div>
                                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                        {(item.reasons && item.reasons.length > 0 ? item.reasons : ['—']).map((r, ri) => (
                                            <span key={ri} style={{ fontSize: 10, color: T.orange, fontWeight: 500, background: 'rgba(217,119,6,0.08)', borderRadius: 4, padding: '2px 6px', whiteSpace: 'nowrap' }}>{r}</span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    */}
                </div>

                {/* Item Revenue Trend — Last 30 Days (commented out: uses mock random data, no real API) */}
                {/* <div style={cardStyle}>
                    <div className="rpt-flex-row rpt-flex-between rpt-flex-center" style={{ marginBottom: 16 }}>
                        <p className="rpt-section-title" style={{ ...cardTitleStyle, marginBottom: 0 }}>Item Revenue Trend — Last 30 Days</p>
                        <SearchableMultiSelect label="Select Items" options={allItemNames} selected={trendItems} onChange={(val) => { if (val.length <= 5) setTrendItems(val); }} placeholder="Search items..." menuAlign="right" />
                    </div>
                    {trendItems.length === 0 ? (
                        <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.t3, fontSize: 13 }}>Select items above to view their revenue trend</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={trendData} margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={T.divider} />
                                <XAxis dataKey="date" tick={{ fontSize: 10, fill: T.t3 }} axisLine={false} tickLine={false} interval={4} />
                                <YAxis tick={{ fontSize: 10, fill: T.t3 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${getCurrencySymbol()}${v}`} />
                                <Tooltip contentStyle={{ fontFamily: T.font, fontSize: 11, borderRadius: 8, border: `1px solid ${T.cardBorder}` }} />
                                {trendItems.map((name, i) => (
                                    <Line key={name} type="monotone" dataKey={name} name={name} stroke={trendLineColors[i % trendLineColors.length]} strokeWidth={2} dot={false} />
                                ))}
                                <Legend iconType="line" iconSize={12} wrapperStyle={{ fontSize: 11, color: T.t2, paddingTop: 8 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div> */}

                <div style={cardStyle}>
                    <div className="rpt-flex-row rpt-flex-between rpt-flex-center" style={{ marginBottom: 12 }}>
                        <div>
                            <p className="rpt-section-title" style={cardTitleStyle}>Full Item Performance Table</p>
                            <p className="rpt-subtitle" style={{ ...cardSubtitleStyle, marginBottom: 0 }}>Click any item row to view detail panel</p>
                        </div>
                        <div className="rpt-flex-row rpt-flex-center rpt-gap-12">
                            <div style={{ fontSize: 11, color: T.t3 }}>
                                {tableLoading
                                    ? <Bone w={130} h={12} r={4} />
                                    : showItemTablePagination
                                        ? `Showing ${(activeTablePage - 1) * ITEM_TABLE_PAGE_SIZE + 1}–${Math.min(activeTablePage * ITEM_TABLE_PAGE_SIZE, activeTotalItems)} of ${activeTotalItems} items`
                                        : `Showing ${activeTableData.length} of ${activeTotalItems} items`}
                            </div>
                            {!tableLoading && paginatedTableData.length > 0 && (
                                <ReportSectionExportButton
                                    dateRangeLabel={exportDateRangeLabel}
                                    variant="desktop"
                                    title="Full Item Performance Table"
                                    filename="Item_Performance_Table"
                                    buildExport={async (format, opts) => {
                                        const all = await fetchAllItemTableRows(opts?.locationId);
                                        const qty = (n: number) => format === 'pdf' ? n.toLocaleString() : n;
                                        const revenue = (n: number) => formatMoneyForExport(n);
                                        return {
                                            headers: ['Item', 'Category', 'Qty Sold', 'Revenue', 'PMIX %'],
                                            rows: all.map((r) => [r.name, r.category, qty(r.qty), revenue(r.revenue), `${r.pmix.toFixed(2)}%`]),
                                        };
                                    }}
                                />
                            )}
                        </div>
                    </div>
                    <div className="rpt-flex-row rpt-gap-8" style={{ marginBottom: 16 }}>
                        <SearchableMultiSelect label="Category" options={allCategories} selected={tableCategoryFilter} onChange={setTableCategoryFilter} placeholder="Search categories..." />
                        <div style={{ position: 'relative', flex: 1 }}>
                            <span className="material-symbols-rounded" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: T.t3, pointerEvents: 'none' }}>search</span>
                            <input
                                type="text"
                                value={itemSearch}
                                onChange={(e) => setItemSearch(e.target.value)}
                                placeholder="Search items..."
                                style={{ width: '100%', height: 34, paddingLeft: 32, paddingRight: itemSearch ? 32 : 12, paddingTop: 0, paddingBottom: 0, border: `1px solid ${T.cardBorder}`, borderRadius: 8, fontSize: 12, fontFamily: T.font, color: T.t1, background: T.surface, outline: 'none', boxSizing: 'border-box' }}
                            />
                            {itemSearch && (
                                <button type="button" onClick={() => setItemSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: T.t3, display: 'flex', alignItems: 'center' }}>
                                    <span className="material-symbols-rounded" style={{ fontSize: 16 }}>close</span>
                                </button>
                            )}
                            {itemSearchLoading && (
                                <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: T.t3 }}>…</span>
                            )}
                        </div>
                    </div>
                    {tableLoading ? (
                        <div style={{ padding: '8px 0' }}>
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} className="rpt-flex-row rpt-flex-center rpt-gap-12" style={{ padding: '12px 0', borderBottom: i < 7 ? `1px solid ${T.divider}` : 'none' }}>
                                    <Bone w={120} h={12} r={4} />
                                    <Bone w={140} h={12} r={4} style={{ flex: 1 }} />
                                    <Bone w={50} h={12} r={4} />
                                    <Bone w={70} h={12} r={4} />
                                    <Bone w={50} h={12} r={4} />
                                </div>
                            ))}
                        </div>
                    ) : paginatedTableData.length === 0 ? (
                        <SectionEmpty kpiName="item performance" size="medium" minHeight={220} />
                    ) : (
                    <DataTable
                        columns={[
                            { key: 'name', label: 'Item', bold: true, sortable: true, sortKey: 'itemName', render: (v) => <button type="button" style={{ cursor: 'pointer', color: T.accent, fontWeight: 600, background: 'none', border: 'none', fontFamily: 'inherit', fontSize: 'inherit', padding: 0 }} onClick={() => setSelectedItem(fullTableData.find((i) => i.name === v))}>{v}</button> },
                            { key: 'category', label: 'Category', color: T.t2 },
                            { key: 'qty', label: 'Qty Sold', align: 'right', sortable: true, sortKey: 'salesQty', render: (v) => fmtQty(v) },
                            { key: 'revenue', label: 'Revenue', align: 'right', sortable: true, sortKey: 'grossSales', render: (v) => fmtFull(v) },
                            { key: 'pmix', label: 'PMIX %', align: 'right', render: (v) => `${v.toFixed(2)}%` },
// { key: 'refundQty', label: 'Refunded Qty', align: 'right', sortable: true, render: (v) => (v > 0 ? <span style={{ color: T.red, fontWeight: 600 }}>{Number(v).toLocaleString()}</span> : <span style={{ color: T.green }}>0</span>) },
                        ]}
                        data={paginatedTableData}
                        sortBy={feSortField}
                        sortAsc={feSortAsc}
                        onSort={handleSortChange}
                    />
                    )}
                    {tableLoading ? (
                        <div className="rpt-flex-row rpt-flex-center rpt-flex-between rpt-flex-wrap rpt-gap-8" style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.divider}` }}>
                            <Bone w={72} h={12} r={4} />
                            <div className="rpt-flex-row rpt-flex-center rpt-gap-6">
                                {Array.from({ length: 4 }).map((_, i) => <Bone key={i} w={30} h={30} r={6} />)}
                            </div>
                        </div>
                    ) : showItemTablePagination ? (
                        <ItemTablePagination
                            page={activeTablePage}
                            totalPages={activeTotalPages}
                            onChange={(p: number) => setActiveTablePage(() => p)}
                            rangeLabel={`${(activeTablePage - 1) * ITEM_TABLE_PAGE_SIZE + 1}–${Math.min(activeTablePage * ITEM_TABLE_PAGE_SIZE, activeTotalItems)} of ${activeTotalItems}`}
                        />
                    ) : null}
                </div>

                <ModifierPerformanceCard branch={branch} locationId={locationId} token={token} startDate={startDate} endDate={endDate} exportDateRangeLabel={exportDateRangeLabel} />

                <div style={{ height: 40 }} />
            </main>

            {/* {selectedItem && <ItemDetailPanel item={selectedItem} onClose={() => setSelectedItem(null)} addonData={addonData} refundData={refundData} channelItems={channelItems} pmixData={pmixData} />} */}
        </>
    );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MOBILE PRODUCT & MENU PERFORMANCE
// ═══════════════════════════════════════════════════════════════════════════════

// ─── Mobile helpers ──────────────────────────────────────────────────────────
const MobileTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: '6px 10px', boxShadow: T.shadow, fontFamily: T.font }}>
            {label && <div style={{ fontSize: 10, fontWeight: 600, color: T.t1, marginBottom: 2 }}>{label}</div>}
            {payload.map((p, i) => {
                // Donut segments (Top Revenue Streams) carry units/avgPerItem on their row —
                // other charts using this same tooltip component don't, so this stays a no-op for them.
                const row = p.payload ?? {};
                const hasStreamDetail = row.units !== undefined || row.avgPerItem !== undefined;
                return (
                    <div key={i} style={{ marginTop: 1 }}>
                        <div style={{ fontSize: 10, color: T.t2, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ width: 6, height: 6, borderRadius: 1.5, background: p.color || T.accent, display: 'inline-block' }} />
                            {p.name}: {fmtFull(p.value)}{row.pct != null ? ` (${Number(row.pct).toFixed(1)}%)` : ''}
                        </div>
                        {hasStreamDetail && (
                            <div style={{ fontSize: 9, color: T.t3, marginLeft: 10 }}>
                                Units: {row.units != null ? row.units : '—'} · Avg/item: {row.avgPerItem != null ? fmtFull(row.avgPerItem) : '—'}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const MobileSearchSelect = ({ label, options, selected, onChange, placeholder = 'Search...' }: any) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const orderWhenOpenedRef = useRef<any>(null);
    const selectedSet = useMemo(() => new Set((selected || []).map((s) => String(s ?? '').trim())), [selected]);
    const filtered = useMemo(() => {
        if (!open) {
            orderWhenOpenedRef.current = null;
            return options;
        }
        const searchLower = search.trim().toLowerCase();
        if (searchLower) {
            return options.filter((o) => (String(o ?? '').trim().toLowerCase()).includes(searchLower));
        }
        if (orderWhenOpenedRef.current === null) {
            const base = [...options];
            const sel = selected || [];
            const setAtOpen = new Set(sel.map((s) => String(s ?? '').trim()));
            if (setAtOpen.size > 0) {
                const selectedFirst = base.filter((o) => setAtOpen.has(String(o ?? '').trim()));
                const rest = base.filter((o) => !setAtOpen.has(String(o ?? '').trim()));
                orderWhenOpenedRef.current = [...selectedFirst, ...rest];
            } else {
                orderWhenOpenedRef.current = base;
            }
        }
        return orderWhenOpenedRef.current ?? options;
    }, [options, search, open, selected]);
    const toggle = (val: any) => {
        const v = String(val ?? '').trim();
        const isCurrentlySelected = selectedSet.has(v);
        onChange(isCurrentlySelected ? (selected || []).filter((s) => String(s ?? '').trim() !== v) : [...(selected || []), val]);
    };
    return (
        <div>
            <button onClick={(e) => { e.stopPropagation(); setOpen(true); }} style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 7,
                border: `1px solid ${(selected || []).length > 0 ? T.accent : T.cardBorder}`,
                background: (selected || []).length > 0 ? T.accentSoft : T.surface,
                fontSize: 10, fontWeight: 500, color: (selected || []).length > 0 ? T.accent : T.t2,
                fontFamily: T.font, cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
                {(selected || []).length === 0 ? label : (selected || []).length === 1 ? ((selected[0] || '').length > 14 ? (selected[0] || '').slice(0, 14) + '…' : selected[0]) : `${(selected || []).length} sel.`}
                <span style={{ fontSize: 7, color: T.t3 }}>▼</span>
            </button>
            {open && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ flex: 1, background: 'rgba(0,0,0,0.3)' }} onClick={() => { setOpen(false); setSearch(''); }} />
                    <div style={{ background: T.surface, borderRadius: '16px 16px 0 0', height: '50vh', minHeight: 280, display: 'flex', flexDirection: 'column', boxShadow: '0 -4px 20px rgba(0,0,0,0.10)', flexShrink: 0, paddingBottom: 'max(6px, env(safe-area-inset-bottom))' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 6px' }}>
                            <div style={{ width: 36, height: 4, borderRadius: 2, background: T.divider }} />
                        </div>
                        <div style={{ padding: '4px 16px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: T.t1 }}>{label}</span>
                            <button onClick={() => { setOpen(false); setSearch(''); }} style={{ border: 'none', background: 'none', fontSize: 13, color: T.accent, fontWeight: 600, fontFamily: T.font, cursor: 'pointer' }}>Done</button>
                        </div>
                        <div style={{ padding: '0 16px 10px' }}>
                            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder={placeholder}
                                style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 10, border: `1px solid ${T.cardBorder}`, fontSize: 13, fontFamily: T.font, color: T.t1, background: T.bg, outline: 'none' }} />
                        </div>
                        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 8px 16px' }}>
                            {filtered.length === 0 && (
                                <div style={{ padding: '16px 16px', fontSize: 13, color: T.t3, textAlign: 'center' }}>
                                    {options.length === 0 ? `No ${label.toLowerCase()} available` : 'No results'}
                                </div>
                            )}
                            {filtered.map(opt => {
                                const optNorm = String(opt ?? '').trim();
                                const isSel = selectedSet.has(optNorm);
                                return (
                                    <div key={opt} onClick={() => toggle(opt)} style={{
                                        padding: '6px 12px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, borderRadius: 8,
                                        background: isSel ? T.accentSoft : 'transparent', color: isSel ? T.accent : T.t1, fontWeight: isSel ? 600 : 400, marginBottom: 0,
                                    }}>
                                        <span style={{ width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${isSel ? T.accent : T.cardBorder}`, background: isSel ? T.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', flexShrink: 0 }}>{isSel ? '✓' : ''}</span>
                                        <span>{opt}</span>
                                    </div>
                                );
                            })}
                        </div>
                        {(selected || []).length > 0 && (
                            <div style={{ padding: '8px 16px max(6px, env(safe-area-inset-bottom))', borderTop: `1px solid ${T.divider}` }}>
                                <button onClick={() => { onChange([]); setSearch(''); }} style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: T.bg, fontSize: 12, fontWeight: 600, color: T.accent, fontFamily: T.font, cursor: 'pointer' }}>Clear All</button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── MobileProductScreen ─────────────────────────────────────────────────────
function MobileProductScreen() {
    useSwipeBack();
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    const setTopBarProps = useDesktopTopBar();

    const [datePeriod, setDatePeriod] = useSharedReportDateFilter('Yesterday', { excludeToday: true });
    const locationId = branch?.id ?? branch?.locationId ?? '';
    const token = auth?.accessToken ?? auth?.access_token ?? null;

    const storeTz = useStoreTimeZone();
    const today = todayStr(storeTz);
    const yesterday = yesterdayStr(storeTz);
    const dateRange = useMemo(() => getStartEndFromFilter(datePeriod, today, yesterday, storeTz), [datePeriod, today, yesterday, storeTz]);
    const exportDateRangeLabel = exportPeriodFor(datePeriod, dateRange)?.label ?? datePeriod;
    const startDate = dateRange?.startDate ?? '';
    const endDate = dateRange?.endDate ?? '';

    useEffect(() => {
        setTopBarProps({
            title: 'Product & Menu',
            dateFilters: { value: datePeriod, onChange: setDatePeriod, options: DATE_PRESETS_MOBILE_NO_TODAY },
        });
    }, [setTopBarProps, datePeriod]);

    // ─── Data state (mirrors desktop) ────────────────────────────────────────
    const [sellerView, setSellerView] = useState('top');
    const [selectedItem, setSelectedItem] = useState<any>(null);
    const [trendItems, setTrendItems] = useState([]);
    const [tableCatFilter, setTableCatFilter] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState<{ categoryName: string; categoryId: string }[]>([]);
    const itemTableHydratedRef = useRef(false);
    const [itemTablePage, setItemTablePage] = useState(1);
    const [mItemSearch, setMItemSearch] = useState('');
    const [mItemSearchRows, setMItemSearchRows] = useState<any[]>([]);
    const [mItemSearchTotal, setMItemSearchTotal] = useState(0);
    const [mItemSearchPagesTotal, setMItemSearchPagesTotal] = useState(1);
    const [mItemSearchPage, setMItemSearchPage] = useState(1);
    const [mItemSearchLoading, setMItemSearchLoading] = useState(false);
    // Client-side sort for Item / Qty Sold / Revenue — see FE_SORT_FIELDS above.
    // Category / PMIX % are not sortable at all.
    const [feSortField, setFeSortField] = useState<string | null>(null);
    const [feSortAsc, setFeSortAsc] = useState(true);
    const [feSortRows, setFeSortRows] = useState<any[] | null>(null);
    const [feSortLoading, setFeSortLoading] = useState(false);
    const [feSortPage, setFeSortPage] = useState(1);
    const handleSortChange = useCallback((field: string) => {
        if (!FE_SORT_FIELDS.has(field)) return;
        if (feSortField === field) setFeSortAsc((a) => !a);
        else { setFeSortField(field); setFeSortAsc(true); }
        setFeSortPage(1);
    }, [feSortField]);

    const [refreshNonce, setRefreshNonce] = useState(0);
    const [topItemsRevenue, setTopItemsRevenue] = useState([]);
    const [bottomItemsRevenue, setBottomItemsRevenue] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const [pmixData, setPmixData] = useState([]);
    // Sales by Category (ZAR-1223) — replaces the PMIX card (mobile).
    const catSales = useCategorySalesData(locationId, token, startDate, endDate);
    const [refundData, setRefundData] = useState([]);
    const [fullTableData, setFullTableData] = useState([]);
    const [itemTableTotal, setItemTableTotal] = useState(0);
    const [itemTablePagesTotal, setItemTablePagesTotal] = useState(1);
    const [itemTableLoading, setItemTableLoading] = useState(false);
    const [categoryItemOverall, setCategoryItemOverall] = useState({ gross: null, qty: null, avgPerItem: null });
    const [productLoading, setProductLoading] = useState(false);
    const [productError, setProductError] = useState<any>(null);
    const [channelItems, setChannelItems] = useState<any[]>([]);
    const [channelSeries, setChannelSeries] = useState<any[]>([]);
    const [channelCurrentPage, setChannelCurrentPage] = useState(1);
    const [channelTotalPages, setChannelTotalPages] = useState(1);
    const [channelTotalElements, setChannelTotalElements] = useState(0);
    const channelPageSize = 10;
    const [channelLoading, setChannelLoading] = useState(false);
    const channelRangeStart = channelTotalElements === 0 ? 0 : (channelCurrentPage - 1) * channelPageSize + 1;
    const channelRangeEnd = channelTotalElements === 0 ? 0 : Math.min(channelCurrentPage * channelPageSize, channelTotalElements);

    // ─── Fetch data (same API calls as desktop) ─────────────────────────────
    useEffect(() => {
        if (!locationId || !token || !startDate || !endDate) return;
        const opts = { locationId, startDate, endDate, token, filter: 'Overall' };
        const cacheKey = getProductCacheKey(locationId, startDate, endDate, 0);
        if (refreshNonce > 0) productDatasetCache.delete(cacheKey);
        const cached = cacheGet(productDatasetCache,cacheKey);
        if (cached) {
            setProductError(null);
            setTopItemsRevenue(cached.topItemsRevenue);
            setBottomItemsRevenue(cached.bottomItemsRevenue);
            setCategoryData(cached.categoryData);
            setCategoryItemOverall(cached.categoryItemOverall);
            setPmixData(cached.pmixData);
            setRefundData(cached.refundData);
            setFullTableData(cached.fullTableData);
            setItemTableTotal(cached.itemTableTotal ?? 0);
            setItemTablePagesTotal(cached.itemTablePages ?? 1);
            return;
        }
        setProductLoading(true);
        setProductError(null);
        const desktopFirstPageKey = getChannelCacheKey(locationId, startDate, endDate, 1, 20);
        const preloadChannel =
            cacheHas(channelDatasetCache,desktopFirstPageKey)
                ? Promise.resolve()
                : getCategoryChannelSummary({
                    locationId,
                    startDate,
                    endDate,
                    page: 0,
                    size: 10,
                    groupByCategory: false,
                    token,
                })
                    .then((res) => {
                        cacheSet(channelDatasetCache,desktopFirstPageKey, mapCategoryChannelSummaryRows(res));
                    })
                    .catch(() => { });
        Promise.all([fetchProductMenuDataset(opts, 0, ITEM_TABLE_PAGE_SIZE_CONST), preloadChannel])
            .then(([dataset]) => {
                cacheSet(productDatasetCache,cacheKey, dataset);
                setTopItemsRevenue(dataset.topItemsRevenue);
                setBottomItemsRevenue(dataset.bottomItemsRevenue);
                setCategoryData(dataset.categoryData);
                setCategoryItemOverall(dataset.categoryItemOverall);
                setPmixData(dataset.pmixData);
                setRefundData(dataset.refundData);
                setFullTableData(dataset.fullTableData);
                setItemTableTotal(dataset.itemTableTotal ?? 0);
                setItemTablePagesTotal(dataset.itemTablePages ?? 1);
            })
            .catch((err) => {
                setProductError(err?.message ?? 'Failed to load product data');
                setTopItemsRevenue([]); setBottomItemsRevenue([]); setCategoryData([]);
                setPmixData([]); setRefundData([]); setFullTableData([]);
                setItemTableTotal(0);
                setItemTablePagesTotal(1);
                setCategoryItemOverall({ gross: null, qty: null, avgPerItem: null });
            })
            .finally(() => setProductLoading(false));
    }, [locationId, startDate, endDate, token, refreshNonce]); // eslint-disable-line react-hooks/exhaustive-deps

    // Category dropdown options come straight from the API (categoryName + categoryId).
    const allCategories = useMemo(
        () => categoryOptions.map((c) => c.categoryName).filter(Boolean).sort(),
        [categoryOptions]
    );
    // Selected category names → comma-joined categoryId string for the item-table API.
    const selectedCategoryIds = useMemo(() => {
        if (tableCatFilter.length === 0) return '';
        const nameToId = new Map(categoryOptions.map((c) => [c.categoryName, c.categoryId]));
        return tableCatFilter.map((name) => nameToId.get(name)).filter(Boolean).join(',');
    }, [tableCatFilter, categoryOptions]);

    // Separate effect: refetch the item table when the page or the category filter changes.
    // The category filter is applied server-side via the categoryId query param.
    useEffect(() => {
        if (feSortField) return; // Item/Qty Sold/Revenue sort is handled client-side (see feSort effect below)
        if (!locationId || !token || !startDate || !endDate) return;
        if (mItemSearch.trim() !== '') return;
        if (itemTablePage === 1 && !selectedCategoryIds) {
            const cached = cacheGet(productDatasetCache,getProductCacheKey(locationId, startDate, endDate, 0));
            if (cached) {
                setFullTableData(cached.fullTableData);
                setItemTableTotal(cached.itemTableTotal ?? 0);
                setItemTablePagesTotal(cached.itemTablePages ?? 1);
                itemTableHydratedRef.current = true;
                return;
            }
            // Cache expired (e.g. a filter was cleared after the TTL). On the very first
            // load the main bundle effect owns this fetch, so skip until hydrated.
            if (!itemTableHydratedRef.current) return;
        }
        const pageIndex = itemTablePage - 1;
        setItemTableLoading(true);
        getProductCategoryItemSales({ locationId, startDate, endDate, page: pageIndex, size: ITEM_TABLE_PAGE_SIZE_CONST, categoryId: selectedCategoryIds, token })
            .then((res) => {
                const rows = getCategoryItemSalesRows(res).map(mapCategoryItemSalesRow);
                setFullTableData(rows);
                setItemTableTotal(readTableTotalItems(res, rows));
                setItemTablePagesTotal(readTableTotalPages(res));
                itemTableHydratedRef.current = true;
            })
            .catch(() => { /* keep current page */ })
            .finally(() => setItemTableLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemTablePage, selectedCategoryIds, feSortField]);

    useEffect(() => {
        setChannelCurrentPage(1);
    }, [locationId, startDate, endDate, token]);

    useEffect(() => {
        if (!locationId || !token || !startDate || !endDate) return;
        const cacheKey = getChannelCacheKey(locationId, startDate, endDate, channelCurrentPage, channelPageSize);
        const cached = cacheGet(channelDatasetCache,cacheKey);
        if (cached) {
            setChannelItems(cached.rows);
            setChannelSeries(cached.series);
            setChannelTotalPages(cached.totalPages);
            setChannelTotalElements(cached.totalElements);
            return;
        }
        setChannelLoading(true);
        getCategoryChannelSummary({
            locationId,
            startDate,
            endDate,
            page: channelCurrentPage - 1,
            size: channelPageSize,
            groupByCategory: false,
            token,
        })
            .then((res) => {
                const mapped = mapCategoryChannelSummaryRows(res);
                cacheSet(channelDatasetCache,cacheKey, mapped);
                setChannelItems(mapped.rows);
                setChannelSeries(mapped.series);
                setChannelTotalPages(mapped.totalPages);
                setChannelTotalElements(mapped.totalElements);
            })
            .catch(() => {
                setChannelItems([]);
                setChannelSeries([]);
                setChannelTotalPages(1);
                setChannelTotalElements(0);
            })
            .finally(() => setChannelLoading(false));
    }, [locationId, startDate, endDate, token, channelCurrentPage, channelPageSize, refreshNonce]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Computed data ───────────────────────────────────────────────────────
    const allItemNames = useMemo(() => {
        const names = new Set();
        topItemsRevenue.forEach((i) => names.add(i.name));
        bottomItemsRevenue.forEach((i) => names.add(i.name));
        fullTableData.forEach((i) => names.add(i.name));
        return Array.from(names);
    }, [topItemsRevenue, bottomItemsRevenue, fullTableData]);

    // Fetch the category dropdown list for the current location + date range.
    useEffect(() => {
        if (!locationId || !token || !startDate || !endDate) return;
        let cancelled = false;
        getCategoryDropdown({ locationId, startDate, endDate, token })
            .then((res) => {
                if (cancelled) return;
                const list = normList(res)
                    .map((c) => ({
                        categoryName: c?.categoryName ?? c?.name ?? '',
                        categoryId: c?.categoryId ?? c?.id ?? '',
                    }))
                    .filter((c) => c.categoryName && c.categoryId);
                setCategoryOptions(list);
            })
            .catch(() => { if (!cancelled) setCategoryOptions([]); });
        return () => { cancelled = true; };
    }, [locationId, startDate, endDate, token]);

    useEffect(() => {
        if (allItemNames.length > 0 && trendItems.length === 0) setTrendItems(allItemNames.slice(0, 3));
    }, [allItemNames.length]);

    // Drop any selected categories that no longer exist for the new date range / location.
    useEffect(() => {
        setTableCatFilter((prev) => {
            if (prev.length === 0) return prev;
            const valid = prev.filter((name) => allCategories.includes(name));
            return valid.length === prev.length ? prev : valid;
        });
    }, [allCategories]);

    // Category filtering is now applied server-side (via categoryId), so the table
    // shows whatever the API returns for the current page + selected categories.
    const filteredTableData = fullTableData;

    const isMItemSearchActive = mItemSearch.trim() !== '';
    const MOBILE_ITEM_TABLE_PAGE_SIZE = ITEM_TABLE_PAGE_SIZE_CONST;

    // When a client-side sort (Item/Qty Sold/Revenue) is active, the fully-fetched +
    // locally-sorted set (feSort*) takes over instead of server-side paging.
    const feSortedRows = useMemo(() => {
        if (!feSortField || !feSortRows) return null;
        const key = FE_SORT_ROW_KEY[feSortField];
        return [...feSortRows].sort((a, b) => compareFeSortRows(a, b, key, feSortAsc));
    }, [feSortField, feSortRows, feSortAsc]);
    const feSortActive = !!feSortField && !!feSortedRows;
    const feSortTotalItems = feSortedRows ? feSortedRows.length : 0;
    const feSortTotalPages = feSortedRows ? Math.max(1, Math.ceil(feSortedRows.length / ITEM_TABLE_PAGE_SIZE_CONST)) : 1;
    const feSortPageRows = useMemo(() => {
        if (!feSortedRows) return [];
        const start = (feSortPage - 1) * ITEM_TABLE_PAGE_SIZE_CONST;
        return feSortedRows.slice(start, start + ITEM_TABLE_PAGE_SIZE_CONST);
    }, [feSortedRows, feSortPage]);

    const mActiveTableData = feSortActive ? feSortPageRows : (isMItemSearchActive ? mItemSearchRows : filteredTableData);
    const mActiveTotalItems = feSortActive ? feSortTotalItems : (isMItemSearchActive ? mItemSearchTotal : itemTableTotal);
    const mActiveTotalPages = feSortActive ? feSortTotalPages : (isMItemSearchActive ? mItemSearchPagesTotal : itemTablePagesTotal);
    const mActiveTablePage = feSortActive ? feSortPage : (isMItemSearchActive ? mItemSearchPage : itemTablePage);
    const setMActiveTablePage = feSortActive ? setFeSortPage : (isMItemSearchActive ? setMItemSearchPage : setItemTablePage);
    const showMobileItemTablePagination = mActiveTotalPages > 1;

    // Export the WHOLE dataset (not just the current page): refetch
    // /product/insights/category-item-sales with size = total items, carrying the
    // active category filter / search so it matches the on-screen scope. The refetch
    // drops whatever order the backend returns, so if an Item/Qty Sold/Revenue
    // client-sort is active, re-apply it here too — otherwise the export would land
    // in a different order than what's on screen.
    const fetchAllItemTableRowsMobile = async (overrideLocationId?: string) => {
        const loc = overrideLocationId ?? locationId;
        let size = mActiveTotalItems || mActiveTableData.length || 1;
        if (overrideLocationId) {
            const probe = await getProductCategoryItemSales({
                locationId: loc, startDate, endDate, token,
                page: 0, size: 1,
                categoryId: selectedCategoryIds || undefined,
                search: isMItemSearchActive ? mItemSearch.trim() : undefined,
            });
            size = (probe as any)?.totalElements ?? (probe as any)?.total ?? size;
        }
        const res = await getProductCategoryItemSales({
            locationId: loc, startDate, endDate, token,
            page: 0, size,
            categoryId: selectedCategoryIds || undefined,
            search: isMItemSearchActive ? mItemSearch.trim() : undefined,
        });
        const rows = getCategoryItemSalesRows(res).map(mapCategoryItemSalesRow);
        if (!feSortField) return rows;
        const key = FE_SORT_ROW_KEY[feSortField];
        return [...rows].sort((a, b) => compareFeSortRows(a, b, key, feSortAsc));
    };

    // Item/Qty Sold/Revenue sort tapped: fetch the whole current scope once (size =
    // current totalItems) — feSortedRows above then sorts it in memory.
    useEffect(() => {
        if (!feSortField) { setFeSortRows(null); setFeSortLoading(false); return; }
        if (!locationId || !token || !startDate || !endDate) return;
        let cancelled = false;
        setFeSortLoading(true);
        fetchAllItemTableRowsMobile()
            .then((rows) => { if (!cancelled) setFeSortRows(rows); })
            .catch(() => { if (!cancelled) setFeSortRows([]); })
            .finally(() => { if (!cancelled) setFeSortLoading(false); });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feSortField, locationId, startDate, endDate, token, selectedCategoryIds, isMItemSearchActive]);

    // Item table is fetching (category/page change, search, or client-sort fetch-all) — shimmer rows + pagination.
    const mTableLoading = feSortLoading || mItemSearchLoading || itemTableLoading;

    const paginatedTableDataMobile = useMemo(() => {
        if (feSortActive) return feSortPageRows;
        if (isMItemSearchActive) return mItemSearchRows;
        return filteredTableData;
    }, [feSortActive, feSortPageRows, isMItemSearchActive, mItemSearchRows, filteredTableData]);

    useEffect(() => { setItemTablePage(1); }, [tableCatFilter, startDate, endDate]);

    // Any change to the active filter/search/date scope invalidates the previously
    // fetched "sort all items" snapshot — drop back to server-driven sort/paging.
    useEffect(() => {
        setFeSortField(null);
        setFeSortRows(null);
        setFeSortPage(1);
    }, [tableCatFilter, startDate, endDate, mItemSearch, locationId]);

    useEffect(() => {
        const trimmed = mItemSearch.trim();
        if (!trimmed) { setMItemSearchRows([]); setMItemSearchTotal(0); setMItemSearchPagesTotal(1); setMItemSearchPage(1); return; }
        if (!locationId || !token) return;
        setMItemSearchPage(1);
        setMItemSearchLoading(true);
        const timer = setTimeout(() => {
            getProductCategoryItemSales({ locationId, startDate, endDate, page: 0, size: MOBILE_ITEM_TABLE_PAGE_SIZE, search: trimmed, token })
                .then((res) => {
                    const rows = getCategoryItemSalesRows(res).map(mapCategoryItemSalesRow);
                    setMItemSearchRows(rows);
                    setMItemSearchTotal(readTableTotalItems(res, rows));
                    setMItemSearchPagesTotal(readTableTotalPages(res));
                })
                .catch(() => { setMItemSearchRows([]); setMItemSearchTotal(0); setMItemSearchPagesTotal(1); })
                .finally(() => setMItemSearchLoading(false));
        }, 400);
        return () => clearTimeout(timer);
    }, [mItemSearch, locationId, startDate, endDate, token]);

    useEffect(() => {
        if (!isMItemSearchActive || mItemSearchPage === 1) return;
        if (!locationId || !token) return;
        setMItemSearchLoading(true);
        getProductCategoryItemSales({ locationId, startDate, endDate, page: mItemSearchPage - 1, size: MOBILE_ITEM_TABLE_PAGE_SIZE, search: mItemSearch.trim(), token })
            .then((res) => {
                const rows = getCategoryItemSalesRows(res).map(mapCategoryItemSalesRow);
                setMItemSearchRows(rows);
                setMItemSearchTotal(readTableTotalItems(res, rows));
                setMItemSearchPagesTotal(readTableTotalPages(res));
            })
            .catch(() => setMItemSearchRows([]))
            .finally(() => setMItemSearchLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mItemSearchPage]);

    const kpiTotalRevenue = useMemo(
        () => (categoryItemOverall.gross != null ? categoryItemOverall.gross : fullTableData.reduce((s, r) => s + r.revenue, 0)),
        [categoryItemOverall.gross, fullTableData]
    );
    const kpiTotalQty = useMemo(
        () => (categoryItemOverall.qty != null ? categoryItemOverall.qty : fullTableData.reduce((s, r) => s + r.qty, 0)),
        [categoryItemOverall.qty, fullTableData]
    );
    const kpiAvgPerItem = useMemo(
        () => (categoryItemOverall.avgPerItem != null ? categoryItemOverall.avgPerItem : (kpiTotalQty > 0 ? kpiTotalRevenue / kpiTotalQty : 0)),
        [categoryItemOverall.avgPerItem, kpiTotalRevenue, kpiTotalQty]
    );

    const sellerData = sellerView === 'top' ? topItemsRevenue.slice(0, 10) : bottomItemsRevenue.slice(0, 5);
    const maxRev = sellerData.length > 0 ? Math.max(...sellerData.map(d => d.revenue)) : 1;
    const mobileRefundRateScaleMax = useMemo(() => Math.max(1, ...refundData.map((r) => Number(r.rate) || 0)), [refundData]);

    const trendLineColors = [T.accent, T.orange, T.green, '#7C5CFC', '#0EA5E9'];
    const trendData = useMemo(() => {
        if (trendItems.length === 0) return [];
        const days = [];
        for (let i = 29; i >= 0; i--) {
            const d = new Date(); d.setDate(d.getDate() - i);
            const row = { date: `${d.getMonth() + 1}/${d.getDate()}` };
            trendItems.forEach((name) => { row[name] = 100 + Math.floor(Math.random() * 200); });
            days.push(row);
        }
        return days;
    }, [trendItems]);

    // ─── Render ──────────────────────────────────────────────────────────────
    const handlePtrRefresh = useCallback(() => setRefreshNonce(n => n + 1), []);
    const { scrollRef: ptrScrollRef, pullDistance, isRefreshing: ptrRefreshing } = usePullToRefresh({ onRefresh: handlePtrRefresh });

    return (
        <Phone>
            <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} />
            <TopBar variant="mobile" />
            {/* Scrollable Content */}
            <div ref={ptrScrollRef} className="rpt-flex-col rpt-gap-8" style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 16px' }}>

                {productError && (
                    <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 10, padding: 10, fontSize: 11, color: T.red }}>{productError}</div>
                )}
                {productLoading ? <ProductMobileShimmer /> : (<>

                {/* KPI Strip */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    <div className="rpt-card--r12 rpt-card" style={{ padding: '12px 10px', textAlign: 'center' }}>
                        <div style={{ fontSize: 8, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>Item Revenue<KpiTooltip text="Item value excluding taxes" isMobile /></div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: T.t1, marginTop: 4 }}>{getCurrencySymbol()}{(Number(kpiTotalRevenue) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                    <div className="rpt-card--r12 rpt-card" style={{ padding: '12px 10px', textAlign: 'center' }}>
                        <div style={{ fontSize: 8, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.3 }}>Items Sold</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: T.t1, marginTop: 4 }}>{fmtNum(kpiTotalQty)}</div>
                    </div>
                    <div className="rpt-card--r12 rpt-card" style={{ padding: '12px 10px', textAlign: 'center' }}>
                        <div style={{ fontSize: 8, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.3 }}>Avg / Item</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: T.t1, marginTop: 4 }}>{getCurrencySymbol()}{(Math.round(kpiAvgPerItem * 100) / 100).toFixed(2)}</div>
                    </div>
                </div>

                {/* Top / Bottom Items */}
                <div style={{ background: T.card, border: `1px solid ${T.accent}33`, borderRadius: 14, padding: 14, position: 'relative', boxShadow: T.shadow }}>
                    <div className="rpt-flex-row rpt-flex-between rpt-flex-center" style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{sellerView === 'top' ? 'Top 10 by Revenue' : 'Bottom 5 by Revenue'}</div>
                        <div className="rpt-flex-row rpt-flex-center rpt-gap-4">
                            {['top', 'bottom'].map(v => (
                                <button key={v} onClick={() => setSellerView(v)} style={{ padding: '3px 8px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 9, fontWeight: 500, fontFamily: T.font, background: sellerView === v ? T.accentSoft : 'transparent', color: sellerView === v ? T.accent : T.t3 }}>{v === 'top' ? 'Top 10' : 'Bottom 5'}</button>
                            ))}
                        </div>
                    </div>
                    {sellerData.length === 0 ? (
                        <SectionEmpty kpiName={sellerView === 'top' ? 'top items by revenue' : 'bottom items by revenue'} minHeight={140} />
                    ) : sellerData.map((item, i) => (
                        <div key={i} onClick={() => setSelectedItem(item)} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7, cursor: 'pointer' }}>
                            <div style={{ width: 18, height: 18, borderRadius: 4, background: i < 3 ? T.accentSoft : T.hover, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 600, color: i < 3 ? T.accent : T.t3, flexShrink: 0 }}>{i + 1}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 11, fontWeight: 500, color: T.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 3, maxWidth: '18ch' }}>{item.name}</div>
                                <div style={{ background: T.hover, borderRadius: 2.5, height: 5, overflow: 'hidden' }}>
                                    <div style={{ width: `${(item.revenue / maxRev) * 100}%`, height: '100%', background: sellerView === 'top' ? T.accent : T.red, borderRadius: 2.5, opacity: 0.7 }} />
                                </div>
                            </div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: T.t1, flexShrink: 0 }}>{fmtFull(item.revenue)}</div>
                        </div>
                    ))}
                </div>

                {/* Top Revenue Streams */}
                <div className="rpt-card" style={{ padding: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>Top Revenue Streams</div>
                        {categoryData.length > 0 && (
                            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                variant="mobile"
                                title="Top Revenue Streams"
                                filename="top-revenue-streams"
                                buildExport={async (format, opts) => {
                                    const cc = branch?.country ?? branch?.countryCode ?? branch?.countryCd;
                                    const money = (n: any) => formatMoneyForExport(n);
                                    // Outlet-scoped export: refetch for the override outlet instead of
                                    // reusing this outlet's already-loaded categoryData (no pagination here).
                                    let data = categoryData;
                                    if (opts?.locationId && opts.locationId !== locationId) {
                                        const r = await getSalesByRevenueClass({ locationId: opts.locationId, startDate, endDate, token, filter: 'Overall' });
                                        const list = normList(r);
                                        const rowTotal = list.reduce((s, row) => s + (pickNumDeep(row, ['totalSales', 'total_sales', 'value', 'revenue', 'amount']) ?? 0), 0);
                                        data = sortRevenueStreams(list.map((row) => mapRevenueStreamRow(row, rowTotal)));
                                    }
                                    const total = data.reduce((s, c) => s + c.value, 0);
                                    const totalUnits = data.reduce((s, c) => s + (c.units ?? 0), 0);
                                    const totalAvgPerItem = totalUnits > 0 ? total / totalUnits : null;
                                    return {
                                        totalRow: true,
                                        headers: ['Stream', 'Revenue', 'Share %', 'Units Sold', 'Avg/Item'],
                                        rows: [
                                            ...data.map((cat: any) => [cat.name, money(cat.value), `${cat.pct.toFixed(2)}%`, cat.units != null ? cat.units : '—', cat.avgPerItem != null ? money(cat.avgPerItem) : '—']),
                                            ['Total', money(total), total > 0 ? '100%' : '—', totalUnits > 0 ? totalUnits : '—', totalAvgPerItem != null ? money(totalAvgPerItem) : '—'],
                                        ],
                                    };
                                }}
                            />
                        )}
                    </div>
                    {categoryData.length === 0 ? (
                        <SectionEmpty kpiName="top revenue streams" minHeight={160} />
                    ) : (
                    <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                        <ResponsiveContainer width="100%" height={160}>
                            <PieChart>
                                <Pie data={categoryData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2} minAngle={3} strokeWidth={0}>
                                    {categoryData.map((_, i) => <Cell key={i} fill={categoryColors[i % categoryColors.length]} />)}
                                </Pie>
                                <Tooltip content={<MobileTooltip />} wrapperStyle={{ zIndex: 100 }} contentStyle={{ zIndex: 100, background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, boxShadow: T.shadow }} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none', zIndex: 0 }}>
                            <div style={{ fontSize: 9, color: T.t3, fontWeight: 500 }}>Total</div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: T.t1, marginTop: 2 }}>{fmtFull(categoryData.reduce((s, c) => s + c.value, 0))}</div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
                        {categoryData.map((cat, i) => (
                            <div key={i}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ width: 8, height: 8, borderRadius: 2, background: categoryColors[i % categoryColors.length], display: 'inline-block', flexShrink: 0 }} />
                                    <span style={{ fontSize: 13, color: T.t1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.name}</span>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: T.t1, marginLeft: 'auto', whiteSpace: 'nowrap' }}>{fmtFull(cat.value)}</span>
                                    <span style={{ fontSize: 12, color: T.t2, whiteSpace: 'nowrap' }}>{cat.pct.toFixed(1)}%</span>
                                </div>
                                <div style={{ fontSize: 11, color: T.t3, marginLeft: 14, marginTop: 2 }}>
                                    {cat.units != null ? `${cat.units} units` : '— units'} · {cat.avgPerItem != null ? `${fmtFull(cat.avgPerItem)} avg` : '— avg'}
                                </div>
                            </div>
                        ))}
                    </div>
                    </>
                    )}
                </div>

                {/* Sales by Category (ZAR-1223) — replaces the PMIX card */}
                <div className="rpt-card" style={{ padding: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>Sales by Category</div>
                            <div style={{ fontSize: 10, color: T.t3, marginTop: 2 }}>Top menu categories by revenue</div>
                        </div>
                        {catSales.totalElements > 0 && (
                            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                variant="mobile"
                                title="Sales by Category"
                                filename="sales-by-category"
                                buildExport={async (format, opts) => {
                                    const cats = await catSales.fetchAll(opts?.locationId);
                                    // Every cell carries its own currency symbol now (formatMoneyForExport),
                                    // so this column is text in every format — no more raw-number CSV branch.
                                    const money = (n: number) => formatMoneyForExport(n);
                                    const totalQty = cats.reduce((s, c) => s + (Number(c.quantity) || 0), 0);
                                    const totalValue = cats.reduce((s, c) => s + (Number(c.price) || 0), 0);
                                    return {
                                        totalRow: true,
                                        headers: ['Serial No.', 'Category Name', 'Total Sales Quantity', 'Total Sales Value'],
                                        rows: [
                                            ...cats.map((item, i) => [i + 1, item.categoryName, fmtExportQty(item.quantity), money(item.price)]),
                                            ['', 'Total', fmtExportQty(totalQty), money(totalValue)],
                                        ],
                                    };
                                }}
                            />
                        )}
                    </div>
                    {catSales.loading ? (
                        <div>
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} style={{ marginBottom: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <Bone w={100} h={11} />
                                        <Bone w={48} h={11} />
                                    </div>
                                    <Bone h={5} r={3} />
                                </div>
                            ))}
                        </div>
                    ) : catSales.failure ? (
                        <SectionError kpiName="sales by category" onRetry={catSales.retry} size="small" minHeight={120} />
                    ) : catSales.items.length === 0 ? (
                        <SectionEmpty kpiName="sales by category" minHeight={120} />
                    ) : (() => {
                        const maxCatPrice = Math.max(...(catSales.allItems.length ? catSales.allItems : catSales.items).map(item => item.price), 1);
                        return (
                            <>
                                {catSales.items.map((item, i) => (
                                    <div key={i} style={{ marginBottom: i < catSales.items.length - 1 ? 9 : 0 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                            <span style={{ fontSize: 12, fontWeight: 500, color: T.t1 }}>{item.categoryName}</span>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{fmtFull(item.price)}</span>
                                        </div>
                                        <div style={{ height: 5, background: T.hover, borderRadius: 3, overflow: 'hidden' }}>
                                            <div style={{ height: 5, borderRadius: 3, width: `${(item.price / maxCatPrice) * 100}%`, background: '#db7172' }} />
                                        </div>
                                    </div>
                                ))}
                                {/* Running total reflects ALL categories, not just this page. */}
                                <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.divider}`, display: 'flex', justifyContent: 'space-between', fontSize: 10, color: T.t3 }}>
                                    <span>{catSales.totalElements} categories</span>
                                    <span>Total: <span style={{ color: T.t1, fontWeight: 600 }}>{fmtFull(catSales.grandTotal)}</span></span>
                                </div>
                                {/* Records-per-page + pagination */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, paddingTop: 8, borderTop: `1px solid ${T.divider}`, gap: 8, flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                        <span style={{ fontSize: 10, color: T.t3 }}>Per page:</span>
                                        <select
                                            className="rpt-pager-select-sm"
                                            value={String(catSales.pageSize)}
                                            onChange={e => catSales.setPageSize(e.target.value === 'All' ? 'All' : Number(e.target.value))}
                                            style={{ height: 24, padding: '0 4px', border: `1px solid ${T.cardBorder}`, borderRadius: 6, fontSize: 11, color: T.t1, background: T.card, cursor: 'pointer' }}
                                        >
                                            {CAT_PAGE_SIZE_OPTIONS.map(n => <option key={String(n)} value={String(n)}>{n}</option>)}
                                        </select>
                                    </div>
                                    {catSales.pageSize !== 'All' && catSales.totalPages > 1 && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                            <button onClick={() => catSales.setPage(p => Math.max(0, p - 1))} disabled={catSales.page === 0} style={{ height: 24, padding: '0 8px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: catSales.page === 0 ? T.hover : T.card, color: catSales.page === 0 ? T.t3 : T.t1, cursor: catSales.page === 0 ? 'default' : 'pointer', fontSize: 11 }}>‹</button>
                                            <span style={{ fontSize: 10, color: T.t3 }}>{catSales.page + 1} / {catSales.totalPages}</span>
                                            <button onClick={() => catSales.setPage(p => Math.min(catSales.totalPages - 1, p + 1))} disabled={catSales.page >= catSales.totalPages - 1} style={{ height: 24, padding: '0 8px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: catSales.page >= catSales.totalPages - 1 ? T.hover : T.card, color: catSales.page >= catSales.totalPages - 1 ? T.t3 : T.t1, cursor: catSales.page >= catSales.totalPages - 1 ? 'default' : 'pointer', fontSize: 11 }}>›</button>
                                        </div>
                                    )}
                                </div>
                            </>
                        );
                    })()}
                </div>

                {/* Item Revenue by Channel */}
                <div className="rpt-card" style={{ padding: 14 }}>
                    <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>Revenue by Channel</div>
                    </div>
                    {channelItems.length === 0 || channelSeries.length === 0 ? (
                        <SectionEmpty kpiName="revenue by channel" minHeight={180} />
                    ) : (
                    <>
                    <ResponsiveContainer width="100%" height={Math.max(220, channelItems.length * 28 + 60)}>
                            <BarChart layout="vertical" data={channelItems} margin={{ left: 4, right: 10, top: 4, bottom: 4 }}>
                                <XAxis
                                    type="number"
                                    tickFormatter={(v) => fmt(v)}
                                    tick={{ fontSize: 9, fill: T.t3 }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={80}
                                    tick={(props) => {
                                        const { x, y, payload } = props;
                                        const s = String(payload?.value ?? '');
                                        const label = s.length > 14 ? s.slice(0, 14) + '…' : s;
                                        return <text x={x} y={y} dy={3} textAnchor="end" fontSize={9} fill={T.t2}>{label}</text>;
                                    }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip content={<MobileTooltip />} wrapperStyle={{ zIndex: 100 }} contentStyle={{ zIndex: 100, background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, boxShadow: T.shadow }} />
                                <Legend iconType="square" iconSize={8} wrapperStyle={{ fontSize: 10, paddingTop: 8 }} />
                                {channelSeries.map((series, idx) => (
                                    <Bar key={series.key} dataKey={series.key} name={series.label} stackId="a" fill={series.color ?? getChannelColor(series.key, idx)} radius={idx === channelSeries.length - 1 ? [0, 3, 3, 0] : undefined} />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                        <div className="rpt-flex-row rpt-flex-between rpt-flex-center rpt-gap-8" style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.divider}` }}>
                            <span style={{ fontSize: 10, color: T.t3 }}>{channelRangeStart}-{channelRangeEnd} of {channelTotalElements}</span>
                            <div className="rpt-flex-row rpt-gap-6">
                                <button type="button" disabled={channelCurrentPage <= 1 || channelLoading} onClick={() => setChannelCurrentPage((p) => Math.max(1, p - 1))} style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: channelCurrentPage <= 1 || channelLoading ? T.hover : T.surface, fontSize: 10, fontWeight: 600, color: T.t2, cursor: channelCurrentPage <= 1 || channelLoading ? 'not-allowed' : 'pointer', opacity: channelCurrentPage <= 1 || channelLoading ? 0.5 : 1 }}>Prev</button>
                                <button type="button" disabled={channelCurrentPage >= channelTotalPages || channelLoading} onClick={() => setChannelCurrentPage((p) => Math.min(channelTotalPages, p + 1))} style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: channelCurrentPage >= channelTotalPages || channelLoading ? T.hover : T.surface, fontSize: 10, fontWeight: 600, color: T.t2, cursor: channelCurrentPage >= channelTotalPages || channelLoading ? 'not-allowed' : 'pointer', opacity: channelCurrentPage >= channelTotalPages || channelLoading ? 0.5 : 1 }}>Next</button>
                            </div>
                        </div>
                    </>
                    )}
                </div>

                {/* Refund & Void Highlights — hidden
                {refundData.length > 0 && (
                    <div className="rpt-card" style={{ padding: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, marginBottom: 10 }}>Refund & Void Highlights</div>
                        {refundData.slice(0, 5).map((item, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 10, fontWeight: 500, color: T.t1, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                                    <div style={{ background: T.hover, borderRadius: 2.5, height: 5, overflow: 'hidden' }}>
                                        <div style={{ width: `${Math.max(2, ((Number(item.rate) || 0) / mobileRefundRateScaleMax) * 75)}%`, height: '100%', background: T.red, borderRadius: 2.5, opacity: 0.6 }} />
                                    </div>
                                </div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: T.red, flexShrink: 0 }}>{(Number(item.rate) || 0).toFixed(2)}%</div>
                                <div style={{ fontSize: 8, fontWeight: 500, color: T.orange, background: 'rgba(217,119,6,0.08)', borderRadius: 4, padding: '2px 5px', whiteSpace: 'nowrap', flexShrink: 0 }}>{item.reason}</div>
                            </div>
                        ))}
                    </div>
                )}
                */}

                {/* Revenue Trend — 30D (commented out: uses mock random data, no real API) */}
                {/* <div className="rpt-card" style={{ padding: 14 }}>
                    <div className="rpt-flex-row rpt-flex-between rpt-flex-center" style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>Revenue Trend — 30D</div>
                        <MobileSearchSelect label="Items" options={allItemNames} selected={trendItems} onChange={(v) => { if (v.length <= 5) setTrendItems(v); }} placeholder="Search items..." />
                    </div>
                    {trendItems.length === 0 ? (
                        <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.t3, fontSize: 11 }}>Select items to view</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={140}>
                            <LineChart data={trendData} margin={{ left: 0, right: 5, top: 5, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke={T.divider} />
                                <XAxis dataKey="date" tick={{ fontSize: 7, fill: T.t3 }} axisLine={false} tickLine={false} interval={6} />
                                <YAxis tick={{ fontSize: 7, fill: T.t3 }} axisLine={false} tickLine={false} tickFormatter={v => `${getCurrencySymbol()}${v}`} width={32} />
                                <Tooltip content={<MobileTooltip />} />
                                {trendItems.map((name, i) => (
                                    <Line key={name} type="monotone" dataKey={name} name={name} stroke={trendLineColors[i % trendLineColors.length]} strokeWidth={1.5} dot={false} />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                    {trendItems.length > 0 && (
                        <div className="rpt-flex-row rpt-flex-wrap" style={{ gap: '3px 10px', marginTop: 4 }}>
                            {trendItems.map((name, i) => (
                                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 8, color: T.t2 }}>
                                    <span style={{ width: 8, height: 2.5, borderRadius: 1, background: trendLineColors[i % trendLineColors.length], display: 'inline-block' }} />
                                    {name.length > 16 ? name.slice(0, 16) + '…' : name}
                                </div>
                            ))}
                        </div>
                    )}
                </div> */}

                {/* Full Item Performance — Card List */}
                <div className="rpt-card" style={{ padding: 14 }}>
                    <div className="rpt-flex-row rpt-flex-between rpt-flex-center" style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>Item Performance</div>
                        <div className="rpt-flex-row rpt-flex-center rpt-gap-4">
                            {mTableLoading ? (
                                <Bone w={56} h={10} r={4} />
                            ) : (
                                <span style={{ fontSize: 9, color: T.t3 }}>
                                    {showMobileItemTablePagination ? `${(mActiveTablePage - 1) * MOBILE_ITEM_TABLE_PAGE_SIZE + 1}–${Math.min(mActiveTablePage * MOBILE_ITEM_TABLE_PAGE_SIZE, mActiveTotalItems)} / ${mActiveTotalItems}` : `${mActiveTableData.length}/${fullTableData.length}`}
                                </span>
                            )}
                            {!mTableLoading && paginatedTableDataMobile.length > 0 && (
                                <ReportSectionExportButton
                                    dateRangeLabel={exportDateRangeLabel}
                                    variant="mobile"
                                    title="Item Performance"
                                    filename="Item_Performance"
                                    buildExport={async (format, opts) => {
                                        const all = await fetchAllItemTableRowsMobile(opts?.locationId);
                                        const qty = (n: number) => format === 'pdf' ? fmtNum(n) : n;
                                        const revenue = (n: number) => formatMoneyForExport(n);
                                        return {
                                            headers: ['Item', 'Category', 'Qty', 'Revenue', 'PMIX%'],
                                            rows: all.map((r) => [r.name, r.category, qty(r.qty), revenue(r.revenue), `${r.pmix.toFixed(2)}%`]),
                                        };
                                    }}
                                />
                            )}
                        </div>
                    </div>
                    <div style={{ marginBottom: 10, display: 'flex', gap: 6, flexDirection: 'column' }}>
                        <MobileSearchSelect label="Category" options={allCategories} selected={tableCatFilter} onChange={setTableCatFilter} placeholder="Search categories..." />
                        <div style={{ position: 'relative' }}>
                            <span className="material-symbols-rounded" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: T.t3, pointerEvents: 'none' }}>search</span>
                            <input
                                type="text"
                                value={mItemSearch}
                                onChange={(e) => setMItemSearch(e.target.value)}
                                placeholder="Search items..."
                                style={{ width: '100%', paddingLeft: 30, paddingRight: mItemSearch ? 30 : 10, paddingTop: 7, paddingBottom: 7, border: `1px solid ${T.cardBorder}`, borderRadius: 8, fontSize: 11, fontFamily: T.font, color: T.t1, background: T.surface, outline: 'none', boxSizing: 'border-box' }}
                            />
                            {mItemSearch && (
                                <button type="button" onClick={() => setMItemSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: T.t3, display: 'flex', alignItems: 'center' }}>
                                    <span className="material-symbols-rounded" style={{ fontSize: 14 }}>close</span>
                                </button>
                            )}
                            {mItemSearchLoading && <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: T.t3 }}>…</span>}
                        </div>
                    </div>
                    {/* Sort chips — Item/Qty/Revenue only, sorted client-side (fetch all, sort in memory). Tap again to flip direction. */}
                    <div className="rpt-flex-row rpt-flex-wrap rpt-gap-4" style={{ marginBottom: 10 }}>
                        <span style={{ fontSize: 9, color: T.t3, alignSelf: 'center', marginRight: 2 }}>Sort:</span>
                        {ITEM_TABLE_SORT_OPTIONS.map((o) => {
                            const active = feSortField === o.field;
                            const activeAsc = feSortAsc;
                            return (
                                <button
                                    key={o.field}
                                    type="button"
                                    onClick={() => handleSortChange(o.field)}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 2,
                                        fontSize: 10, fontWeight: 600, fontFamily: T.font,
                                        padding: '4px 8px', borderRadius: 6, boxSizing: 'border-box',
                                        border: `1px solid ${active ? T.accent : T.cardBorder}`,
                                        background: active ? T.accent : T.surface,
                                        color: active ? '#fff' : T.t2, cursor: 'pointer',
                                    }}
                                >
                                    {o.label}{active ? (activeAsc ? ' ↑' : ' ↓') : ''}
                                </button>
                            );
                        })}
                    </div>
                    {mTableLoading ? (
                        Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} style={{ padding: '10px 12px', borderRadius: 10, border: `1px solid ${T.divider}`, background: T.card, marginBottom: 6 }}>
                                <div className="rpt-flex-row rpt-flex-between" style={{ marginBottom: 7 }}>
                                    <div style={{ flex: 1 }}>
                                        <Bone w={120} h={12} r={4} mb={5} />
                                        <Bone w={70} h={9} r={4} />
                                    </div>
                                    <Bone w={56} h={14} r={4} />
                                </div>
                                <div className="rpt-flex-row rpt-gap-12">
                                    <Bone w={44} h={9} r={4} />
                                    <Bone w={52} h={9} r={4} />
                                </div>
                            </div>
                        ))
                    ) : paginatedTableDataMobile.length === 0 ? (
                        <SectionEmpty kpiName="item performance" minHeight={160} />
                    ) : paginatedTableDataMobile.map((item, i) => (
                        <div key={i} onClick={() => setSelectedItem(item)} style={{
                            padding: '10px 12px', borderRadius: 10, border: `1px solid ${T.accent}25`,
                            background: T.card, cursor: 'pointer', position: 'relative', marginBottom: 6,
                        }}>
                            <div className="rpt-flex-row rpt-flex-between" style={{ marginBottom: 5 }}>
                                <div>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, maxWidth: '18ch', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                                    <div style={{ fontSize: 9, color: T.t3, marginTop: 1 }}>{item.category}</div>
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: T.t1 }}>{fmtFull(item.revenue)}</div>
                            </div>
                            <div className="rpt-flex-row rpt-gap-12">
                                <span style={{ fontSize: 9, color: T.t3 }}>Qty <span style={{ fontWeight: 600, color: T.t2 }}>{fmtQty(item.qty)}</span></span>
                                <span style={{ fontSize: 9, color: T.t3 }}>PMIX <span style={{ fontWeight: 600, color: T.t2 }}>{item.pmix.toFixed(2)}%</span></span>
                                {item.refundQty > 0 && <span style={{ fontSize: 9, color: T.t3 }}>Ref <span style={{ fontWeight: 600, color: T.red }}>{item.refundQty}</span></span>}
                            </div>
                        </div>
                    ))}
                    {mTableLoading ? (
                        <div className="rpt-flex-row rpt-flex-center rpt-flex-between rpt-gap-8" style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.divider}` }}>
                            <Bone w={60} h={10} r={4} />
                            <div className="rpt-flex-row rpt-flex-center rpt-gap-6">
                                {Array.from({ length: 4 }).map((_, i) => <Bone key={i} w={26} h={26} r={6} />)}
                            </div>
                        </div>
                    ) : showMobileItemTablePagination ? (
                        <ItemTablePagination
                            compact
                            page={mActiveTablePage}
                            totalPages={mActiveTotalPages}
                            onChange={(p: number) => setMActiveTablePage(() => p)}
                            rangeLabel={`${(mActiveTablePage - 1) * MOBILE_ITEM_TABLE_PAGE_SIZE + 1}–${Math.min(mActiveTablePage * MOBILE_ITEM_TABLE_PAGE_SIZE, mActiveTotalItems)} of ${mActiveTotalItems}`}
                        />
                    ) : null}
                </div>

                <MobileModifierPerformanceCard branch={branch} locationId={locationId} token={token} startDate={startDate} endDate={endDate} exportDateRangeLabel={exportDateRangeLabel} />

                <div style={{ height: 80 }} />
                </>)}
            </div>

            <BottomNav />

            {/* Item Detail Bottom Sheet — commented out */}
            {/* {selectedItem && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 100, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ flex: 1, background: 'rgba(0,0,0,0.35)' }} onClick={() => setSelectedItem(null)} />
                    <div style={{ background: T.surface, borderRadius: '18px 18px 0 0', maxHeight: '75%', overflowY: 'auto', boxShadow: '0 -4px 24px rgba(0,0,0,0.12)' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
                            <div style={{ width: 36, height: 4, borderRadius: 2, background: T.divider }} />
                        </div>
                        <div style={{ padding: '4px 20px 12px' }}>
                            <div style={{ fontSize: 10, color: T.t3, marginBottom: 3 }}>Item Detail</div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: T.t1 }}>{selectedItem.name}</div>
                        </div>
                        <div style={{ padding: '0 16px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                                {[
                                    { l: 'Revenue', v: fmtFull(selectedItem.revenue) },
                                    { l: 'Qty Sold', v: fmtNum(selectedItem.qty) },
                                    { l: 'PMIX', v: `${(pmixData.find(p => p.name === selectedItem.name)?.pct || selectedItem.pmix || 0).toFixed(2)}%` },
                                ].map((k, i) => (
                                    <div key={i} style={{ background: T.bg, borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
                                        <div style={{ fontSize: 7, color: T.t3, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 5 }}>{k.l}</div>
                                        <div style={{ fontSize: 16, fontWeight: 700, color: T.t1 }}>{k.v}</div>
                                    </div>
                                ))}
                            </div>
                            {(() => {
                                const r = refundData.find(x => x.name === selectedItem.name);
                                return r ? (
                                    <div style={{ background: T.card, border: `1px solid ${T.red}22`, borderRadius: 12, padding: 12, boxShadow: T.shadow }}>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: T.red, marginBottom: 8 }}>Refund Activity</div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.t2, marginBottom: 4 }}><span>Rate</span><span style={{ fontWeight: 600, color: T.red }}>{r.rate.toFixed(2)}%</span></div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.t2, marginBottom: 4 }}><span>Qty</span><span style={{ fontWeight: 600, color: T.t1 }}>{r.qty}</span></div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: T.t2 }}><span>Top Reason</span><span style={{ fontWeight: 500, color: T.orange }}>{r.reason}</span></div>
                                    </div>
                                ) : (
                                    <div style={{ background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.12)', borderRadius: 12, padding: 12 }}>
                                        <div style={{ fontSize: 11, color: T.green, fontWeight: 500 }}>No refunds recorded</div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )} */}
        </Phone>
    );
}

// ─── Main Screen ─────────────────────────────────────────────────────────────
export default function ProductMenuPerformanceScreen() {
    const isDesktop = useIsDesktop();
    return isDesktop ? (
        <DesktopLayout>
            <ProductMenuContent />
        </DesktopLayout>
    ) : (
        <MobileProductScreen />
    );
}

